<template>
  <div>
    <error-box v-if="error" @reload="load()" />

    <loader v-else-if="loading" />

    <template v-if="!error && !loading && tournament">
      <el-alert
        v-if="tournament.registrationOpen === true"
        type="warning"
        show-icon
        :closable="false"
        class="mb-20"
      >
        {{ $tc('tournament.bracket_generation_requires_closed_registrations') }}
      </el-alert>

      <div
        v-if="!hasBrackets && !generating"
        class="empty-bracket-placeholder"
      >
        <div class="empty-bracket-placeholder__content">
          Žreb trenutno nije kreiran. Da biste kreirali žreb, kliknite na dugme 'Generiši žreb'.
        </div>
      </div>

      <div
        v-if="hasBrackets"
        class="bracket-controls"
      >
        <div class="controls-right">
          <div class="bracket-filters">
            <el-button
              :class="{ 'filter-active': bracketFilter === 'individual' }"
              @click="setBracketFilter('individual')"
              plain
            >
              Pojedinačno
            </el-button>
            <el-button
              :class="{ 'filter-active': bracketFilter === 'team' }"
              @click="setBracketFilter('team')"
              plain
            >
              Ekipno
            </el-button>
          </div>
        </div>
      </div>

      <div class="bracket-content-container">
        <div class="brackets-content">
        <div v-if="hasBrackets" class="bracket-root">
          <div
            v-for="categoryGroup in groupedBracketsByCategory"
            :key="categoryGroup.id"
            class="category-group"
          >
            <div class="category-group__header">
              <h2 class="category-group__title">{{ categoryGroup.name }}</h2>
            </div>
            <div
              v-for="bracket in categoryGroup.brackets"
              :key="bracket.id"
              :id="`bracket-card-${bracket.id}`"
              class="bracket-card"
              ref="bracketRef"
            >
              <div class="bracket-card__header">
                <h3 class="bracket-title">
                  {{ getBracketTitle(bracket) }}
                </h3>
                <div class="bracket-card__header-actions">
                  <label class="tatami-label">Tatami</label>
                  <el-select
                    :value="bracketTatami[bracket.id]"
                    placeholder=""
                    class="tatami-select"
                    clearable
                    @input="setBracketTatami(bracket.id, $event)"
                  >
                    <el-option
                      v-for="n in 6"
                      :key="n"
                      :label="String(n)"
                      :value="n"
                    />
                  </el-select>
                  <el-dropdown
                    trigger="click"
                    placement="bottom"
                    @command="(cmd) => handlePrintDropdownCommand(cmd, bracket)"
                  >
                    <button
                      class="print-bracket-btn"
                      type="button"
                      title="Štampaj"
                    >
                      <i class="el-icon-printer"></i>
                    </button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="bracket">{{ $tc('tournament.bracket') || 'Bracket' }}</el-dropdown-item>
                      <el-dropdown-item command="winner_diplomas">{{ $tc('tournament.winner_diplomas') || 'Winner Diplomas' }}</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                  <el-dropdown
                    v-if="user.isAdmin"
                    trigger="click"
                    placement="bottom"
                    @command="(cmd) => handleDrawDropdownCommand(cmd, bracket)"
                  >
                    <button
                      type="button"
                      class="draw-dropdown-btn"
                      :disabled="regeneratingBracketId === bracket.id"
                    >
                      <i v-if="regeneratingBracketId === bracket.id" class="el-icon-loading"></i>
                      <i v-else class="el-icon-more"></i>
                    </button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="regenerate_draw">
                        {{ $tc('tournament.regenerate_draw') }}
                      </el-dropdown-item>
                      <el-dropdown-item command="draw_model">
                        {{ $tc('tournament.draw_model') }}
                      </el-dropdown-item>
                      <el-dropdown-item command="placement">
                        Plasman
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </div>
              <div class="bracket-card__body" :data-bracket-id="bracket.id">
                <div :id="`bracket-card-print-root-${bracket.id}`">
                  <template v-if="isBracketGeneralRanking(bracket)">
                    <template v-if="getBracketDrawModel(bracket) === 'scoring'">
                      <div class="bodovanje-section">
                        <table class="bodovanje-table">
                          <thead>
                            <tr>
                              <th class="bodovanje-col-rb">Rb.</th>
                              <th class="bodovanje-col-name">Ime i prezime</th>
                              <th class="bodovanje-col-disciplina"></th>
                              <th class="bodovanje-col-score">S1</th>
                              <th class="bodovanje-col-score">S2</th>
                              <th class="bodovanje-col-score">S3</th>
                              <th class="bodovanje-col-score">S4</th>
                              <th class="bodovanje-col-score">S5</th>
                              <th class="bodovanje-col-score">S6</th>
                              <th class="bodovanje-col-zbir">Zbir</th>
                              <th class="bodovanje-col-plasman">Plasman</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(competitor, index) in getBodovanjeCompetitors(bracket)"
                              :key="competitor._id || competitor.id"
                            >
                              <td class="bodovanje-col-rb">{{ index + 1 }}</td>
                              <td class="bodovanje-col-name">{{ getBodovanjeCompetitorName(competitor) }}</td>
                              <td class="bodovanje-col-disciplina"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-zbir"></td>
                              <td class="bodovanje-col-plasman"></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <bracket-display
                        :bracket="bracket"
                        :is-locked="areBracketsLocked"
                        :is-admin="user.isAdmin"
                        :competitors="competitors"
                        :teams="teams"
                        :placement-multi="true"
                        @result-submitted="handleResultSubmitted"
                      />
                    </template>
                    <template v-else>
                      <bracket-display
                        :bracket="bracket"
                        :is-locked="areBracketsLocked"
                        :is-admin="user.isAdmin"
                        :competitors="competitors"
                        :teams="teams"
                        :placement-multi="true"
                        @result-submitted="handleResultSubmitted"
                      />
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="getBracketDrawModel(bracket) === 'scoring'">
                      <div class="bodovanje-section">
                        <table class="bodovanje-table">
                          <thead>
                            <tr>
                              <th class="bodovanje-col-rb">Rb.</th>
                              <th class="bodovanje-col-name">Ime i prezime</th>
                              <th class="bodovanje-col-disciplina"></th>
                              <th class="bodovanje-col-score">S1</th>
                              <th class="bodovanje-col-score">S2</th>
                              <th class="bodovanje-col-score">S3</th>
                              <th class="bodovanje-col-score">S4</th>
                              <th class="bodovanje-col-score">S5</th>
                              <th class="bodovanje-col-score">S6</th>
                              <th class="bodovanje-col-zbir">Zbir</th>
                              <th class="bodovanje-col-plasman">Plasman</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(competitor, index) in getBodovanjeCompetitors(bracket)"
                              :key="competitor._id || competitor.id"
                            >
                              <td class="bodovanje-col-rb">{{ index + 1 }}</td>
                              <td class="bodovanje-col-name">{{ getBodovanjeCompetitorName(competitor) }}</td>
                              <td class="bodovanje-col-disciplina"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-score"></td>
                              <td class="bodovanje-col-zbir"></td>
                              <td class="bodovanje-col-plasman"></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <bracket-display
                        :bracket="bracket"
                        :is-locked="areBracketsLocked"
                        :is-admin="user.isAdmin"
                        :competitors="competitors"
                        :teams="teams"
                        :placement-only="true"
                        @result-submitted="handleResultSubmitted"
                      />
                    </template>
                    <template v-else>
                      <bracket-display
                        :bracket="bracket"
                        :is-locked="areBracketsLocked"
                        :is-admin="user.isAdmin"
                        :competitors="competitors"
                        :teams="teams"
                        @result-submitted="handleResultSubmitted"
                      />
                    </template>
                  </template>
                </div>
                <div class="competitor-table-section" :id="`competitors-table-visible-${bracket.id}`">
                  <bracket-competitor-list
                    :key="`competitor-list-visible-${bracket.id}`"
                    :title="getBracketPrintTitle(bracket)"
                    :category-name="bracket.category ? bracket.category.name : ''"
                    :gender-text="getGenderText(bracket)"
                    :competitors="getBracketCompetitors(bracket)"
                    :is-team="isTeamBracket(bracket)"
                    :show-competitor-image="printTableOptions.showCompetitorImage"
                    :show-club-logo="printTableOptions.showClubLogo"
                  />
                </div>
              </div>
              <!-- Hidden competitor table for individual bracket printing -->
              <div style="display: none;">
                <bracket-competitor-list
                  :id="`competitors-table-for-print-${bracket.id}`"
                  :key="`competitor-list-${bracket.id}`"
                  :title="getBracketPrintTitle(bracket)"
                  :category-name="bracket.category ? bracket.category.name : ''"
                  :gender-text="getGenderText(bracket)"
                  :competitors="getBracketCompetitors(bracket)"
                  :is-team="isTeamBracket(bracket)"
                  :show-competitor-image="printTableOptions.showCompetitorImage"
                  :show-club-logo="printTableOptions.showClubLogo"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="!generating" class="empty-state">
          <empty :message="emptyStateMessage" />
        </div>
        </div>
      </div>

      <!-- Print-only layout: matches grouped by rounds -->
      <div v-if="hasBrackets" class="print-brackets-layout" style="display: none;">
        <div
          v-for="categoryGroup in groupedBracketsByCategory"
          :key="`print-category-${categoryGroup.id}`"
          class="print-category-group"
        >
          <div class="print-category-group__header">
            <h1 class="print-category-group__title">{{ categoryGroup.name }}</h1>
          </div>
          <div
            v-for="bracket in categoryGroup.brackets"
            :key="`print-${bracket.id}`"
            class="print-discipline-section"
          >
          <!-- Bracket header: Category → Discipline -->
          <div class="print-discipline-header">
            <h1 class="print-discipline-name">{{ getBracketTitle(bracket) }}</h1>
          </div>

          <!-- Matches grouped by rounds -->
          <div
            v-for="round in getBracketRounds(bracket)"
            :key="`round-${bracket.id}-${round.number}`"
            class="print-round-section"
          >
            <h2 class="print-round-title">{{ getRoundName(round.number, bracket) }}</h2>
            <div
              v-for="match in round.matches"
              :key="`print-match-${match.id}`"
              class="print-match-item"
            >
              <div class="print-match-name">Match {{ getMatchLabelForPrint(match, bracket) }}</div>
              <div class="print-match-competitors">
                <span
                  class="print-competitor"
                  :class="{ 'is-winner': match.competitorReg && match.winnerRegId === match.competitorRegId }"
                >
                  <span v-if="match.competitorReg">
                    <template v-if="isTeamBracket(bracket) && isTeamCompetitor(match.competitorReg)">
                      <span class="print-competitor-team">
                        <span class="print-competitor-club print-competitor-club-main">{{ getClubNameForPrint(match.competitorReg) || '—' }}</span>
                        <span class="print-competitor-names-sub">{{ getCompetitorNameForPrint(match.competitorReg) }}</span>
                      </span>
                    </template>
                    <template v-else>
                      <span class="print-competitor-name">{{ getCompetitorNameForPrint(match.competitorReg) }}</span>
                      <span v-if="getClubNameForPrint(match.competitorReg)" class="print-competitor-club">{{ getClubNameForPrint(match.competitorReg) }}</span>
                    </template>
                    <span v-if="isMedalHolder(match.competitorReg)"> 🏅</span>
                  </span>
                  <span v-else-if="match.isBye" class="print-bye">BYE</span>
                  <span v-else-if="getPlaceholderTextForPrint(match, bracket, 1)" class="print-placeholder">
                    {{ getPlaceholderTextForPrint(match, bracket, 1) }}
                  </span>
                  <span v-else class="print-empty">-</span>
                </span>
                <span class="print-vs">vs</span>
                <span
                  class="print-competitor"
                  :class="{ 'is-winner': match.competitorTwoReg && match.winnerRegId === match.competitorTwoRegId }"
                >
                  <span v-if="match.competitorTwoReg">
                    <template v-if="isTeamBracket(bracket) && isTeamCompetitor(match.competitorTwoReg)">
                      <span class="print-competitor-team">
                        <span class="print-competitor-club print-competitor-club-main">{{ getClubNameForPrint(match.competitorTwoReg) || '—' }}</span>
                        <span class="print-competitor-names-sub">{{ getCompetitorNameForPrint(match.competitorTwoReg) }}</span>
                      </span>
                    </template>
                    <template v-else>
                      <span class="print-competitor-name">{{ getCompetitorNameForPrint(match.competitorTwoReg) }}</span>
                      <span v-if="getClubNameForPrint(match.competitorTwoReg)" class="print-competitor-club">{{ getClubNameForPrint(match.competitorTwoReg) }}</span>
                    </template>
                    <span v-if="isMedalHolder(match.competitorTwoReg)"> 🏅</span>
                  </span>
                  <span v-else-if="match.isBye" class="print-bye">BYE</span>
                  <span v-else-if="getPlaceholderTextForPrint(match, bracket, 2)" class="print-placeholder">
                    {{ getPlaceholderTextForPrint(match, bracket, 2) }}
                  </span>
                  <span v-else class="print-empty">-</span>
                </span>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      <!-- Print layout for filtered brackets with competitor lists -->
      <div v-if="hasBrackets && printConfig" class="print-filtered-layout" style="display: none;">
        <div
          v-for="item in getPrintItems()"
          :key="`print-item-${item.id}`"
          class="print-item-group"
        >
          <!-- Category header (only when printing by categories) -->
          <div
            v-if="printConfig.type === 'categories'"
            class="print-category-header"
          >
            <h1 class="print-category-title">{{ item.name }}</h1>
          </div>

          <!-- Print each bracket (discipline) in the group - each on new page with title + draw + competitor table -->
          <div
            v-for="(bracket, bracketIndex) in item.brackets"
            :key="`print-bracket-${bracket.id}-${printLayoutKey}`"
            class="print-bracket-wrapper"
            :class="{
              'is-first-discipline': bracketIndex === 0 && printConfig.type === 'categories',
              'print-bracket-compact': hasLargeBracket(bracket)
            }"
          >
            <!-- Discipline block: title + draw kept together (no page break between them) -->
            <div class="print-discipline-block">
              <div class="print-bracket-header">
                <h2 class="print-discipline-title">{{ getDisciplineName(bracket) }}</h2>
                <div v-if="printConfig.type === 'categories' && bracket.category" class="print-bracket-subtitle">
                  {{ bracket.category.name }}
                  <span v-if="getGenderText(bracket)" class="gender-text">{{ getGenderText(bracket) }}</span>
                </div>
              </div>
              <div
                class="print-bracket-display-wrapper"
                :class="{ 'print-bracket-compact': hasLargeBracket(bracket) && !isTeamBracket(bracket) }"
              >
                <bracket-display
                  :bracket="bracket"
                  :is-locked="areBracketsLocked"
                  :is-admin="false"
                  :competitors="competitors"
                  :teams="teams"
                  :compact-print="hasLargeBracket(bracket) && !isTeamBracket(bracket)"
                  :print-team-layout="isTeamBracket(bracket)"
                />
              </div>

              <!-- Placement section (print - show saved standings) -->
              <div class="print-placement-section">
                <h4 class="print-placement-title">Plasman:</h4>
                <div class="print-placement-fields">
                  <div v-if="getPrintPlacementCount(bracket) >= 1" class="print-placement-field">
                    <span class="print-placement-label">Prvo mesto:</span>
                    <span class="print-placement-box">
                      <span class="print-placement-line print-placement-line-primary">{{ getPrintPlacementPrimaryLine(bracket, 'first') }}</span>
                      <span class="print-placement-line print-placement-line-secondary">{{ getPrintPlacementSecondaryLine(bracket, 'first') }}</span>
                    </span>
                  </div>
                  <div v-if="getPrintPlacementCount(bracket) >= 2" class="print-placement-field">
                    <span class="print-placement-label">Drugo mesto:</span>
                    <span class="print-placement-box">
                      <span class="print-placement-line print-placement-line-primary">{{ getPrintPlacementPrimaryLine(bracket, 'second') }}</span>
                      <span class="print-placement-line print-placement-line-secondary">{{ getPrintPlacementSecondaryLine(bracket, 'second') }}</span>
                    </span>
                  </div>
                  <div v-if="getPrintPlacementCount(bracket) >= 3" class="print-placement-field">
                    <span class="print-placement-label">Trece mesto:</span>
                    <span class="print-placement-box">
                      <span class="print-placement-line print-placement-line-primary">{{ getPrintPlacementPrimaryLine(bracket, 'third') }}</span>
                      <span class="print-placement-line print-placement-line-secondary">{{ getPrintPlacementSecondaryLine(bracket, 'third') }}</span>
                    </span>
                  </div>
                  <div v-if="getPrintPlacementCount(bracket) >= 4" class="print-placement-field">
                    <span class="print-placement-label">Trece mesto #2:</span>
                    <span class="print-placement-box">
                      <span class="print-placement-line print-placement-line-primary">{{ getPrintPlacementPrimaryLine(bracket, 'third2') }}</span>
                      <span class="print-placement-line print-placement-line-secondary">{{ getPrintPlacementSecondaryLine(bracket, 'third2') }}</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Signature section below draw -->
              <div class="print-signature-section">
                <div class="print-signature-item print-signature-left">
                  <span class="print-signature-label">{{ $tc('tournament.recorder') }}</span>
                  <span class="print-signature-line" />
                </div>
                <div class="print-signature-item print-signature-center">
                  <span class="print-signature-label">{{ $tc('tournament.verified_by') }}</span>
                  <span class="print-signature-line" />
                </div>
                <div class="print-signature-item print-signature-right">
                  <span class="print-signature-label">{{ $tc('tournament.chief_judge') }}</span>
                  <span class="print-signature-line" />
                </div>
              </div>
            </div>

            <!-- Competitor list for this discipline (starts on new page) -->
            <div class="competitor-list-wrapper">
              <bracket-competitor-list
                :id="`competitors-table-for-print-${bracket.id}`"
                :key="`competitor-list-${bracket.id}`"
                :title="getBracketPrintTitle(bracket)"
                :category-name="bracket.category ? bracket.category.name : ''"
                :gender-text="getGenderText(bracket)"
                :competitors="getBracketCompetitors(bracket)"
                :is-team="isTeamBracket(bracket)"
                :show-competitor-image="printTableOptions.showCompetitorImage"
                :show-club-logo="printTableOptions.showClubLogo"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <el-dialog
      :visible.sync="showPrintPreferencesModal"
      title="Opcije štampe"
      width="440px"
      :close-on-click-modal="false"
    >
      <div class="print-preferences-options">
        <el-checkbox v-model="pendingPrintTableOptions.showCompetitorImage">
          Prikaži sliku takmičara
        </el-checkbox>
        <el-checkbox v-model="pendingPrintTableOptions.showClubLogo">
          Prikaži grb kluba
        </el-checkbox>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closePrintPreferencesModal">Otkaži</el-button>
        <el-button type="primary" @click="confirmPrintPreferences">Nastavi</el-button>
      </span>
    </el-dialog>

    <!-- Print target modal -->
    <el-dialog
      :visible.sync="showPrintTypeModal"
      title="Izaberite šta želite da štampate"
      width="440px"
      :close-on-click-modal="false"
    >
      <div class="print-type-options">
        <el-button class="print-type-option-btn" @click="handlePrintTypeSelect('brackets')">
          Štampaj žrebove
        </el-button>
        <el-button class="print-type-option-btn" @click="handlePrintTypeSelect('placements')">
          Štampaj plasmane
        </el-button>
      </div>
    </el-dialog>

    <!-- Existing bracket print options modal -->
    <bracket-print-modal
      :visible.sync="showPrintModal"
      :brackets="filteredBrackets"
      :tournament-id="tournamentId"
      @print="handlePrintSelection"
      @close="handlePrintModalClose"
    />

    <!-- Hidden PrintDocuments for single-bracket diploma print -->
    <div v-show="false" ref="printDocumentsWrap">
      <print-documents
        v-if="currentDocumentType"
        ref="printDocumentsComponent"
        :data="documentPrintData"
        :document-type="currentDocumentType"
        target="competitors"
        entity-type="tournament"
        :competition-name="tournament && tournament.name"
      />
    </div>

    <!-- Model žreba popup: Kostur / Bodovanje / Generalni plasman -->
    <el-dialog
      :visible.sync="showDrawModelModal"
      :title="$tc('tournament.draw_model')"
      width="440px"
      class="draw-model-dialog"
      @close="onDrawModelModalClose"
    >
      <div class="draw-model-options">
        <div class="draw-model-option-wrap">
          <el-button
            class="draw-model-option-btn"
            :type="selectedDrawModelChoice === 'skeleton' ? 'primary' : 'default'"
            @click="selectedDrawModelChoice = 'skeleton'; drawModelBaseChoice = 'skeleton'"
          >
            {{ $tc('tournament.draw_model_skeleton') }}
          </el-button>
        </div>
        <div class="draw-model-option-wrap">
          <el-button
            class="draw-model-option-btn"
            :type="selectedDrawModelChoice === 'scoring' ? 'primary' : 'default'"
            @click="selectedDrawModelChoice = 'scoring'; drawModelBaseChoice = 'scoring'"
          >
            {{ $tc('tournament.scoring') }}
          </el-button>
        </div>
        <div class="draw-model-option-wrap">
          <el-button
            class="draw-model-option-btn"
            :type="selectedDrawModelChoice === 'general' ? 'primary' : 'default'"
            @click="selectedDrawModelChoice = 'general'"
          >
            {{ $tc('tournament.general_overall_ranking') }}
          </el-button>
        </div>
      </div>
      <span slot="footer" class="dialog-footer draw-model-dialog-footer">
        <el-button @click="showDrawModelModal = false">{{ $tc('general.cancel') || 'Cancel' }}</el-button>
        <el-button type="primary" @click="handleSaveDrawModelChoice">
          {{ $tc('general.save_changes') }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="showPlacementModal"
      title="Plasman"
      width="760px"
      class="placement-dialog"
      @close="onPlacementModalClose"
    >
      <div class="placement-modal-content">
        <div v-if="placementModalRows.length === 0" class="placement-empty">
          Nema dodeljenih plasmana za ovaj žreb.
        </div>
        <el-table
          v-else
          :data="placementModalRows"
          border
          stripe
          size="small"
          class="placement-table"
        >
          <el-table-column
            prop="positionLabel"
            label="Plasman"
            width="120"
            header-align="left"
            align="left"
          />
          <el-table-column
            prop="firstName"
            label="Ime"
            min-width="150"
            header-align="left"
            align="left"
          />
          <el-table-column
            prop="lastName"
            label="Prezime"
            min-width="170"
            header-align="left"
            align="left"
          />
          <el-table-column
            prop="club"
            label="Klub"
            min-width="200"
            header-align="left"
            align="left"
          />
          <el-table-column
            prop="medal"
            label="Medalja"
            width="100"
            header-align="left"
            align="left"
          >
            <template slot-scope="scope">
              <span class="placement-medal-icon">{{ scope.row.medal }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer placement-dialog-footer">
        <el-button @click="showPlacementModal = false">Otkaži</el-button>
        <el-button
          type="primary"
          :disabled="placementModalRows.length === 0"
          @click="handlePrintPlacement"
        >
          Štampaj plasman
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Loader from '@/components/other/Loader'
import ErrorBox from '@/components/other/ErrorBox'
import Empty from '@/components/other/Empty'
import BracketDisplay from '@/components/tournaments/BracketDisplay'
import BracketPrintModal from '@/components/tournaments/BracketPrintModal'
import BracketCompetitorList from '@/components/tournaments/BracketCompetitorList'
import PrintDocuments from '@/components/documents/PrintDocuments.vue'
import { generateBracketPrintHTML, generateScoringPrintHTML } from '@/common/bracket-skeleton'
import openPrint from '@/common/print'

export default {
  components: {
    Loader,
    ErrorBox,
    Empty,
    BracketDisplay,
    BracketPrintModal,
    BracketCompetitorList,
    PrintDocuments
  },

  data () {
    return {
      error: false,
      loading: false,
      generating: false,
      resetting: false,
      regeneratingBracketId: null,
      generatingMissing: false,
      brackets: [],
      locking: false,
      unlocking: false,
      bracketFilter: null, // null = all, 'individual' = pojedinačno, 'team' = ekipno
      competitors: [],
      teams: [],
      showPrintPreferencesModal: false,
      showPrintTypeModal: false,
      showPrintModal: false,
      selectedPrintTarget: 'brackets',
      printActionContext: null,
      printTableOptions: {
        showCompetitorImage: true,
        showClubLogo: true
      },
      pendingPrintTableOptions: {
        showCompetitorImage: true,
        showClubLogo: true
      },
      printConfig: null,
      printLayoutKey: 0,
      bracketTatami: {},
      currentDocumentType: null,
      documentPrintData: [],
      showDrawModelModal: false,
      drawModelBracket: null,
      drawModelBaseChoice: 'skeleton',
      selectedDrawModelChoice: 'skeleton',
      bracketDrawModel: {},
      bracketGeneralRanking: {},
      bracketScoringOrder: {},
      showPlacementModal: false,
      placementModalBracket: null
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    },

    viewingActiveTournament () {
      return this.$route.name === 'ActiveTournament'
    },

    activeTournament () {
      return this.$store.state.app.activeTournament
    },

    tournamentId () {
      return this.viewingActiveTournament
        ? this.activeTournament?.id
        : this.$route.params.id
    },

    tournament () {
      if (this.viewingActiveTournament) return this.activeTournament

      return this.$store.getters['tournaments/get'](this.tournamentId)
    },

    hasBrackets () {
      return this.brackets && this.brackets.length > 0
    },

    canGenerateBrackets () {
      return this.user.isAdmin && !this.hasBrackets && this.tournament?.registrationOpen === false
    },

    areBracketsLocked () {
      // Handle boolean, number, or string formats from API
      const locked = this.tournament?.brackets_locked
      return locked === true || locked === 1 || locked === '1'
    },

    filteredBrackets () {
      if (!this.bracketFilter) {
        return this.brackets
      }

      return this.brackets.filter(bracket => {
        // Check if bracket is for team disciplines
        // Brackets have either discipline (individual) or teamDiscipline (team)
        // A bracket is a team bracket if it has teamDiscipline property set (and it's not null/undefined/empty)
        // teamDiscipline will be a number (disciplineId) for team brackets, or null for individual brackets
        const isTeam = bracket.teamDiscipline !== null &&
                       bracket.teamDiscipline !== undefined &&
                       bracket.teamDiscipline !== '' &&
                       bracket.teamDiscipline !== 0

        if (this.bracketFilter === 'team') {
          return isTeam
        } else if (this.bracketFilter === 'individual') {
          // Individual brackets have discipline but no teamDiscipline (or teamDiscipline is null)
          return !isTeam && (bracket.discipline !== null && bracket.discipline !== undefined)
        }

        return true
      })
    },

    groupedBracketsByCategory () {
      const brackets = this.filteredBrackets
      const categoryMap = {}

      // Group brackets by category
      brackets.forEach(bracket => {
        const categoryId = bracket.category?.id || 'uncategorized'
        const categoryName = bracket.category?.name || 'Uncategorized'
        const ageFrom = bracket.category?.ageFrom || 0

        if (!categoryMap[categoryId]) {
          categoryMap[categoryId] = {
            id: categoryId,
            name: categoryName,
            ageFrom: ageFrom,
            brackets: []
          }
        }

        categoryMap[categoryId].brackets.push(bracket)
      })

      // Sort categories youngest first, then oldest (reverse of previous order)
      const categories = Object.values(categoryMap).sort((a, b) => {
        if (a.ageFrom != null && b.ageFrom != null) {
          return b.ageFrom - a.ageFrom
        }
        return (a.name || '').localeCompare(b.name || '')
      })

      // Sort brackets within each category by discipline name
      categories.forEach(category => {
        category.brackets.sort((a, b) => {
          // Get discipline name for individual brackets
          const disciplineA = a.discipline?.name || ''
          const disciplineB = b.discipline?.name || ''

          // For team brackets, use teamDiscipline ID as fallback
          const teamDisciplineA = a.teamDiscipline?.name || (a.teamDiscipline ? String(a.teamDiscipline) : '')
          const teamDisciplineB = b.teamDiscipline?.name || (b.teamDiscipline ? String(b.teamDiscipline) : '')

          const nameA = disciplineA || teamDisciplineA
          const nameB = disciplineB || teamDisciplineB

          return nameA.localeCompare(nameB)
        })
      })

      return categories
    },

    emptyStateMessage () {
      return "Žreb trenutno nije napravljen. Da biste kreirali žreb, kliknite na dugme 'Generiši žreb'."
    },

    placementModalRows () {
      if (!this.placementModalBracket) return []
      return this.getPlacementRowsForBracket(this.placementModalBracket)
    }
  },

  watch: {
    '$route.params.id' () {
      if (!this.viewingActiveTournament) {
        this.load()
        this.setPageData()
      }
    },

    '$route.query.overall' (bracketId) {
      if (bracketId) {
        this.$nextTick(() => this.scrollToOverallFromQuery())
      }
    }
  },

  created () {
    this.load()
  },

  methods: {
    setPageData () {
      let breadcrumbs = null

      if (!this.viewingActiveTournament) {
        breadcrumbs = [
          {
            text: 'Turniri',
            to: { name: 'Tournaments' }
          },
          {
            text: this.tournament?.name,
            to: { name: 'TournamentView', params: { id: this.tournamentId } }
          },
          {
            text: 'Žreb'
          }
        ]
      } else {
        breadcrumbs = [
          {
            text: this.tournament?.name,
            to: { name: 'ActiveTournament' }
          },
          {
            text: 'Žreb'
          }
        ]
      }

      // Set bracket actions for header
      const bracketActions = this.hasBrackets ? {
        notice: !this.areBracketsLocked ? this.$tc('tournament.lock_bracket_to_enter_results') : null,
        showReset: this.user.isAdmin && this.tournament.registrationOpen === false && !this.areBracketsLocked,
        showGenerateMissing: this.user.isAdmin && this.tournament.registrationOpen === false,
        generatingMissing: this.generatingMissing,
        onGenerateMissing: this.handleGenerateMissing,
        showLock: this.user.isAdmin && this.tournament.registrationOpen === false,
        showPrint: true,
        isLocked: this.areBracketsLocked,
        resetting: this.resetting,
        locking: this.locking,
        unlocking: this.unlocking,
        onReset: this.handleReset,
        onLock: this.areBracketsLocked ? this.handleUnlock : this.handleLock,
        onPrint: this.handlePrint
      } : null

      // Set generate bracket action for header
      const generateBracketAction = this.canGenerateBrackets ? {
        generating: this.generating,
        onGenerate: this.handleGenerate
      } : null

      this.$store.commit('app/set', {
        title: `${this.tournament?.name} - Žreb`,
        breadcrumbs,
        bracketActions,
        generateBracketAction
      })
    },

    async load () {
      this.loading = true
      this.loadPersistedTatami()
      this.loadPersistedDrawModel()
      this.loadPersistedGeneralRanking()
      this.loadPersistedScoringOrder()
      try {
        await this.$store.dispatch('tournaments/get', this.tournamentId)
        await Promise.all([
          this.loadBrackets(),
          this.loadCompetitors()
        ])
        this.setPageData()
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
      this.$nextTick(() => this.scrollToOverallFromQuery())
    },

    async loadCompetitors () {
      try {
        const [competitors, teams] = await Promise.all([
          this.$store.dispatch('tournaments/getCompetitors', this.tournamentId),
          this.$store.dispatch('tournaments/getTeams', this.tournamentId)
        ])
        this.competitors = competitors || []
        this.teams = teams || []
      } catch (e) {
        console.error(e)
        this.competitors = []
        this.teams = []
      }
    },

    async reloadTournament () {
      // Reload tournament data to get updated brackets_locked state
      await this.$store.dispatch('tournaments/get', this.tournamentId)
      this.setPageData()
    },

    async loadBrackets () {
      try {
        const response = await this.$store.dispatch(
          'tournaments/getBrackets',
          this.tournamentId
        )
        this.brackets = response || []
        this.applyServerDrawModels(this.brackets)
        this.setPageData()
      } catch (e) {
        console.error(e)
        this.brackets = []
        this.setPageData()
      }
    },

    async handleGenerate () {
      this.$confirm(
        this.$tc('tournament.bracket_generation_warning'),
        this.$tc('general.warning'),
        {
          confirmButtonText: this.$tc('general.confirm'),
          cancelButtonText: this.$tc('general.cancel'),
          type: 'warning'
        }
      )
        .then(async () => {
          this.generating = true
          this.setPageData()
          try {
            await this.$store.dispatch(
              'tournaments/generateBrackets',
              this.tournamentId
            )
            this.$message.success(
              this.$tc('tournament.brackets_generated_successfully')
            )
            await this.loadBrackets()
          } catch (e) {
            console.error(e)
            const errorMessage =
              e.response?.data?.message ||
              e.response?.data?.error ||
              this.$tc('tournament.bracket_generation_failed')
            this.$message.error(errorMessage)
          } finally {
            this.generating = false
            this.setPageData()
          }
        })
        .catch(() => {
          // User cancelled
        })
    },

    async handleGenerateMissing () {
      this.$confirm(
        this.$tc('tournament.generate_missing_draws_warning'),
        this.$tc('general.warning'),
        {
          confirmButtonText: this.$tc('general.confirm'),
          cancelButtonText: this.$tc('general.cancel'),
          type: 'warning'
        }
      )
        .then(async () => {
          this.generatingMissing = true
          this.setPageData()
          try {
            const result = await this.$store.dispatch(
              'tournaments/generateMissingBrackets',
              this.tournamentId
            )
            this.$message.success(
              this.$t('tournament.missing_draws_generated', { count: result?.bracketsGenerated || 0 })
            )
            await this.loadBrackets()
          } catch (e) {
            console.error(e)
            const errorMessage =
              e.response?.data?.message ||
              e.response?.data?.error ||
              this.$tc('tournament.bracket_generation_failed')
            this.$message.error(errorMessage)
          } finally {
            this.generatingMissing = false
            this.setPageData()
          }
        })
        .catch(() => {
          // User cancelled
        })
    },

    async handleReset () {
      this.$confirm(
        this.$tc('tournament.bracket_reset_warning'),
        this.$tc('general.warning'),
        {
          confirmButtonText: this.$tc('general.confirm'),
          cancelButtonText: this.$tc('general.cancel'),
          type: 'error'
        }
      )
        .then(async () => {
          this.resetting = true
          try {
            await this.$store.dispatch(
              'tournaments/resetBrackets',
              this.tournamentId
            )
            this.$message.success(
              this.$tc('tournament.brackets_reset_successfully')
            )
            await this.loadBrackets()
          } catch (e) {
            console.error(e)
            const errorMessage =
              e.response?.data?.message ||
              e.response?.data?.error ||
              this.$tc('tournament.bracket_reset_failed')
            this.$message.error(errorMessage)
          } finally {
            this.resetting = false
          }
        })
        .catch(() => {
          // User cancelled
        })
    },

    getTatamiStorageKey () {
      return `bracket_tatami_${this.tournamentId}`
    },

    getDrawModelStorageKey () {
      return `bracket_draw_model_${this.tournamentId}`
    },

    loadPersistedDrawModel () {
      try {
        const key = this.getDrawModelStorageKey()
        const raw = localStorage.getItem(key)
        if (raw) {
          const obj = JSON.parse(raw)
          if (obj && typeof obj === 'object') {
            this.bracketDrawModel = { ...obj }
            return
          }
        }
        this.bracketDrawModel = {}
      } catch (e) {
        this.bracketDrawModel = {}
      }
    },

    persistBracketDrawModel () {
      try {
        const key = this.getDrawModelStorageKey()
        const toSave = { ...this.bracketDrawModel }
        Object.keys(toSave).forEach(id => {
          const v = toSave[id]
          if (v !== 'skeleton' && v !== 'scoring') delete toSave[id]
        })
        localStorage.setItem(key, JSON.stringify(toSave))
      } catch (e) {
        // Ignore storage errors
      }
    },

    /**
     * The server value wins once a draw model was saved there. Brackets that only have a legacy
     * localStorage choice (set before the model was stored server-side) are pushed to the server
     * once, so the choice survives other devices, other admins and cleared browser data.
     */
    applyServerDrawModels (brackets) {
      const toSync = []
      brackets.forEach(bracket => {
        if (bracket.drawModel === 'skeleton' || bracket.drawModel === 'scoring') {
          this.$set(this.bracketDrawModel, bracket.id, bracket.drawModel)
          this.$set(this.bracketGeneralRanking, bracket.id, bracket.generalRanking === true)
          return
        }
        const localModel = this.bracketDrawModel[bracket.id]
        const localGeneral = this.bracketGeneralRanking[bracket.id] === true
        if (localModel === 'scoring' || localGeneral) {
          toSync.push({ bracketId: bracket.id, drawModel: localModel || 'skeleton', generalRanking: localGeneral })
        }
      })
      this.persistBracketDrawModel()
      this.persistBracketGeneralRanking()
      if (this.user.isAdmin && toSync.length > 0) {
        Promise.all(toSync.map(item => this.$store.dispatch('tournaments/updateBracketDrawModel', item)))
          .catch(e => console.error('Failed to sync draw models to server', e))
      }
    },

    getGeneralRankingStorageKey () {
      return `bracket_general_ranking_${this.tournamentId}`
    },

    loadPersistedGeneralRanking () {
      try {
        const key = this.getGeneralRankingStorageKey()
        const raw = localStorage.getItem(key)
        if (raw) {
          const obj = JSON.parse(raw)
          if (obj && typeof obj === 'object') {
            this.bracketGeneralRanking = { ...obj }
            return
          }
        }
        this.bracketGeneralRanking = {}
      } catch (e) {
        this.bracketGeneralRanking = {}
      }
    },

    persistBracketGeneralRanking () {
      try {
        const key = this.getGeneralRankingStorageKey()
        const toSave = {}
        Object.keys(this.bracketGeneralRanking).forEach(id => {
          if (this.bracketGeneralRanking[id] === true) toSave[id] = true
        })
        localStorage.setItem(key, JSON.stringify(toSave))
      } catch (e) {
        // Ignore storage errors
      }
    },

    getScoringOrderStorageKey () {
      return `bracket_scoring_order_${this.tournamentId}`
    },

    loadPersistedScoringOrder () {
      try {
        const key = this.getScoringOrderStorageKey()
        const raw = localStorage.getItem(key)
        if (raw) {
          const obj = JSON.parse(raw)
          if (obj && typeof obj === 'object') {
            this.bracketScoringOrder = { ...obj }
            return
          }
        }
        this.bracketScoringOrder = {}
      } catch (e) {
        this.bracketScoringOrder = {}
      }
    },

    persistBracketScoringOrder () {
      try {
        const key = this.getScoringOrderStorageKey()
        const toSave = {}
        Object.keys(this.bracketScoringOrder).forEach(id => {
          const arr = this.bracketScoringOrder[id]
          if (Array.isArray(arr) && arr.length > 0) toSave[id] = arr
        })
        localStorage.setItem(key, JSON.stringify(toSave))
      } catch (e) {
        // Ignore storage errors
      }
    },

    loadPersistedTatami () {
      try {
        const key = this.getTatamiStorageKey()
        const raw = localStorage.getItem(key)
        if (raw) {
          const obj = JSON.parse(raw)
          if (obj && typeof obj === 'object') {
            this.bracketTatami = { ...obj }
            return
          }
        }
        this.bracketTatami = {}
      } catch (e) {
        this.bracketTatami = {}
      }
    },

    persistBracketTatami () {
      try {
        const key = this.getTatamiStorageKey()
        const toSave = { ...this.bracketTatami }
        Object.keys(toSave).forEach(id => {
          const v = toSave[id]
          if (v == null || v === '') delete toSave[id]
        })
        localStorage.setItem(key, JSON.stringify(toSave))
      } catch (e) {
        // Ignore storage errors
      }
    },

    setBracketTatami (bracketId, value) {
      this.$set(this.bracketTatami, bracketId, value)
      this.persistBracketTatami()
    },

    handleDrawDropdownCommand (cmd, bracket) {
      if (cmd === 'regenerate_draw') {
        // Always full server reset (matches, placements, points) — same for tree, scoring, and general ranking views.
        this.handleRegenerateBracket(bracket)
      } else if (cmd === 'draw_model') {
        this.drawModelBracket = bracket
        const layout = this.bracketDrawModel[bracket.id] || 'skeleton'
        this.drawModelBaseChoice = layout
        this.selectedDrawModelChoice = this.isBracketGeneralRanking(bracket)
          ? 'general'
          : layout
        this.showDrawModelModal = true
      } else if (cmd === 'placement') {
        this.placementModalBracket = bracket
        this.showPlacementModal = true
      }
    },

    onDrawModelModalClose () {
      this.drawModelBracket = null
    },

    onPlacementModalClose () {
      this.placementModalBracket = null
    },

    /**
     * Drop local-only preferences for a bracket (draw model, general ranking flag, scoring row order, tatami).
     * Call after regenerate: bracket id changes, so old keys must not leak into the new bracket.
     */
    clearPersistedStateForBracket (bracketId) {
      if (bracketId == null || bracketId === '') return
      const id = bracketId
      if (this.bracketScoringOrder[id] !== undefined) this.$delete(this.bracketScoringOrder, id)
      if (this.bracketDrawModel[id] !== undefined) this.$delete(this.bracketDrawModel, id)
      if (this.bracketGeneralRanking[id] !== undefined) this.$delete(this.bracketGeneralRanking, id)
      if (this.bracketTatami[id] !== undefined) this.$delete(this.bracketTatami, id)
      this.persistBracketScoringOrder()
      this.persistBracketDrawModel()
      this.persistBracketGeneralRanking()
      this.persistBracketTatami()
    },

    async handleSaveDrawModelChoice () {
      if (!this.drawModelBracket) return
      const b = this.drawModelBracket
      const generalRanking = this.selectedDrawModelChoice === 'general'
      const drawModel = generalRanking ? this.drawModelBaseChoice : this.selectedDrawModelChoice
      try {
        await this.$store.dispatch('tournaments/updateBracketDrawModel', {
          bracketId: b.id,
          drawModel,
          generalRanking
        })
      } catch (e) {
        console.error(e)
        this.$message.error(e.response?.data?.error || this.$tc('general.save_failed'))
        return
      }
      this.$set(this.bracketGeneralRanking, b.id, generalRanking)
      this.$set(this.bracketDrawModel, b.id, drawModel)
      this.persistBracketGeneralRanking()
      this.persistBracketDrawModel()
      this.showDrawModelModal = false
      this.drawModelBracket = null
      await this.loadBrackets()
    },

    async handleRegenerateBracket (bracket) {
      const disciplineName = bracket.discipline?.name || 'this discipline'
      this.$confirm(
        `Are you sure you want to regenerate the draw for ${disciplineName}? This will delete all matches, clear all ranking placements, remove placement points, and create a fresh draw.`,
        this.$tc('general.warning'),
        {
          confirmButtonText: this.$tc('general.confirm'),
          cancelButtonText: this.$tc('general.cancel'),
          type: 'warning'
        }
      )
        .then(async () => {
          this.regeneratingBracketId = bracket.id
          try {
            await this.$store.dispatch(
              'tournaments/regenerateBracket',
              bracket.id
            )
            this.clearPersistedStateForBracket(bracket.id)
            this.$message.success(
              `Draw for ${disciplineName} has been regenerated successfully.`
            )
            await this.loadBrackets()
          } catch (e) {
            console.error(e)
            const errorMessage =
              e.response?.data?.message ||
              e.response?.data?.error ||
              'Failed to regenerate draw'
            this.$message.error(errorMessage)
          } finally {
            this.regeneratingBracketId = null
          }
        })
        .catch(() => {
          // User cancelled
        })
    },

    handlePrintSingleBracket (bracket) {
      if (this.isBracketGeneralRanking(bracket)) {
        this.$nextTick(() => {
          const main = this.$el.querySelector(`#bracket-card-print-root-${bracket.id}`)
          const competitorsSource = this.$el.querySelector(`#competitors-table-visible-${bracket.id}`) ||
            this.$el.querySelector(`#competitors-table-for-print-${bracket.id}`)
          if (!main) return
          const tempDiv = document.createElement('div')
          tempDiv.appendChild(main.cloneNode(true))
          if (competitorsSource) {
            const pageBreak = document.createElement('div')
            pageBreak.style.pageBreakBefore = 'always'
            tempDiv.appendChild(pageBreak)
            tempDiv.appendChild(competitorsSource.cloneNode(true))
          }
          const printTitle = this.getBracketTitle(bracket) || 'Bracket'
          openPrint(printTitle, tempDiv)
        })
        return
      }

      const disciplineName = this.getDisciplineName(bracket)
      const categoryName = bracket.category?.name || ''

      const placementLabels = {}
      if (bracket.placement) {
        if (bracket.placement.first) placementLabels.first = this.getPrintPlacementLabel(bracket, 'first')
        if (bracket.placement.second) placementLabels.second = this.getPrintPlacementLabel(bracket, 'second')
        if (bracket.placement.third) placementLabels.third = this.getPrintPlacementLabel(bracket, 'third')
        if (bracket.placement.third2) placementLabels.third2 = this.getPrintPlacementLabel(bracket, 'third2')
      }

      const isScoring = this.getBracketDrawModel(bracket) === 'scoring'
      let mainHTML
      if (isScoring) {
        const competitorNames = this.getBodovanjeCompetitors(bracket).map(c => this.getBodovanjeCompetitorName(c))
        mainHTML = generateScoringPrintHTML(
          bracket,
          placementLabels,
          this.tournament,
          categoryName,
          disciplineName,
          this.bracketTatami[bracket.id],
          competitorNames
        )
      } else {
        mainHTML = generateBracketPrintHTML(
          bracket,
          placementLabels,
          this.tournament,
          null,
          categoryName,
          disciplineName,
          this.bracketTatami[bracket.id]
        )
      }

      this.$nextTick(() => {
        const competitorsSource = this.$el.querySelector(`#competitors-table-visible-${bracket.id}`) ||
                                  this.$el.querySelector(`#competitors-table-for-print-${bracket.id}`)
        let allHTML = mainHTML

        if (competitorsSource) {
          const tableClone = competitorsSource.cloneNode(true)
          const pageBreak = document.createElement('div')
          pageBreak.style.pageBreakBefore = 'always'

          // Create a temporary container to get the HTML
          const tempContainer = document.createElement('div')
          tempContainer.appendChild(pageBreak)
          tempContainer.appendChild(tableClone)
          allHTML += tempContainer.innerHTML
        }

        // Create a temporary element with the HTML
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = allHTML
        tempDiv.style.display = 'none'
        document.body.appendChild(tempDiv)

        // Use openPrint to create new window and print
        const printTitle = this.getBracketTitle(bracket) || 'Bracket'
        openPrint(printTitle, tempDiv)

        // Clean up
        setTimeout(() => {
          if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv)
          }
        }, 1000)
      })
    },

    handlePrintDropdownCommand (command, bracket) {
      if (command === 'bracket') {
        this.openPrintPreferencesModal({ type: 'single-bracket', bracket })
        return
      }
      if (command === 'winner_diplomas') {
        this.handlePrintWinnerDiplomas(bracket)
      }
    },

    handlePrintPlacement () {
      if (!this.placementModalBracket) return
      const bracket = this.placementModalBracket
      const placementHtml = this.getPlacementPrintHtml(bracket)
      if (!placementHtml) {
        this.$message.warning('Nema dodeljenih plasmana za štampu.')
        return
      }

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = placementHtml
      tempDiv.style.display = 'none'
      document.body.appendChild(tempDiv)

      openPrint(this.getBracketTitle(bracket) || 'Plasman', tempDiv)

      setTimeout(() => {
        if (document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv)
        }
      }, 1000)
    },

    scrollToOverallFromQuery () {
      const bid = this.$route.query.overall
      if (!bid || !this.hasBrackets) return
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById(`bracket-card-${bid}`)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          const q = { ...this.$route.query }
          if (q.overall == null) return
          delete q.overall
          this.$router.replace({ path: this.$route.path, query: q }).catch(() => {})
        })
      })
    },

    buildDiplomaEntitiesForBracket (bracket) {
      const placementKeys = ['first', 'second', 'third', 'third2']
      const placementLabels = { first: 1, second: 2, third: 3, third2: 3 }
      const placement = bracket.placement || {}
      const placementMulti = bracket.placement_multi || {}

      const regIdsByKey = {}
      placementKeys.forEach((key) => {
        const ids = []
        // 1) Legacy bracket placement (single)
        if (placement[key] != null && placement[key] !== '') {
          ids.push(placement[key])
        }
        // 2) Generalni plasman placement (multi)
        const multiIds = placementMulti[key]
        if (Array.isArray(multiIds) && multiIds.length > 0) {
          multiIds.forEach((id) => {
            if (id == null || id === '') return
            ids.push(id)
          })
        }
        // Unique per slot
        regIdsByKey[key] = Array.from(new Set(ids.map((v) => String(v)))).map(v => parseInt(v))
      })

      const hasAnyWinner = placementKeys.some(key => regIdsByKey[key] && regIdsByKey[key].length > 0)
      if (!hasAnyWinner) return []

      const list = this.getBracketCompetitors(bracket)
      const discipline = bracket.discipline || bracket.teamDiscipline
      const disciplineName = discipline?.name || ''
      const categoryName = bracket.category?.name || ''
      const isTeam = this.isTeamBracket(bracket)
      const byCompetitorId = new Map()

      const upsertEntity = (competitorId, entity) => {
        if (!competitorId) return
        const key = String(competitorId)
        const existing = byCompetitorId.get(key)
        if (!existing) {
          byCompetitorId.set(key, entity)
          return
        }
        // Prefer better placement (1 < 2 < 3)
        const existingPlacement = existing.diplomaPlacement || 999
        const newPlacement = entity.diplomaPlacement || 999
        if (newPlacement < existingPlacement) {
          byCompetitorId.set(key, entity)
        }
      }

      placementKeys.forEach(key => {
        const placementNum = placementLabels[key]
        const ids = regIdsByKey[key] || []
        if (ids.length === 0) return

        ids.forEach((regId) => {
          if (regId == null || regId === '') return
          const reg = list.find(c => c._id != null && String(c._id) === String(regId))
          if (!reg) return

          if (isTeam && reg.isTeam && reg.teamMembers && reg.teamMembers.length > 0) {
            reg.teamMembers.forEach((member) => {
              const competitorId = member.competitorId ?? member.id
              upsertEntity(competitorId, {
                competitor: {
                  id: competitorId,
                  firstName: member.firstName || '',
                  lastName: member.lastName || ''
                },
                club: reg.club || null,
                registration: reg,
                diplomaPlacement: placementNum,
                diplomaDiscipline: disciplineName,
                diplomaCategory: categoryName
              })
            })
          } else if (reg.competitor && reg.competitor.id != null) {
            const competitorId = reg.competitor.id
            upsertEntity(competitorId, {
              competitor: reg.competitor || null,
              club: reg.club || null,
              registration: reg,
              diplomaPlacement: placementNum,
              diplomaDiscipline: disciplineName,
              diplomaCategory: categoryName
            })
          }
        })
      })
      return Array.from(byCompetitorId.values())
    },

    async handlePrintWinnerDiplomas (bracket) {
      const entities = this.buildDiplomaEntitiesForBracket(bracket)
      if (entities.length === 0) {
        this.$message.warning(this.$tc('tournament.diploma_requires_placement') || 'Diplomas require at least one assigned winner in a bracket.')
        return
      }

      const types = await this.$store.dispatch('tournaments/getDocumentTypes', this.tournament.id)
      const documentType = (types || []).find(t => (t.name || '').trim().toLowerCase() === 'diploma')
      if (!documentType) {
        this.$message.warning(this.$tc('tournament.diploma_document_type_missing') || 'Create a "Diploma" document type first (in the Sertifikati panel) to print winner diplomas.')
        return
      }
      documentType.entityId = this.tournament.id

      // Force PrintDocuments to unmount/remount so a second print in the
      // same session re-fetches this type's image instead of reusing a
      // previous type's cached one (same pattern as TournamentPanel).
      this.currentDocumentType = null
      await this.$nextTick()

      this.currentDocumentType = documentType
      this.documentPrintData = entities
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 500))

      const imageReady = await this.waitForDocumentImageReady()
      if (!imageReady || this.$refs.printDocumentsComponent?.error) {
        this.$message.error(
          `${this.$tc('tournament.document_image_load_failed') || 'Failed to load the document image for'} "${documentType.name}"`
        )
        this.currentDocumentType = null
        return
      }

      const html = this.captureDocumentPrintHtml()
      this.currentDocumentType = null
      if (html) {
        this.printCertificatesOnlyHtml(html)
      }
    },

    // PrintDocuments renders <canvas> elements; canvas pixel data is not
    // serialized by innerHTML, so grabbing the wrap's raw HTML would print
    // blank pages. Convert each rendered canvas to a PNG <img> instead.
    captureDocumentPrintHtml () {
      const wrap = this.$refs.printDocumentsWrap
      if (!wrap) return ''
      const canvases = wrap.querySelectorAll('canvas')
      let html = ''
      canvases.forEach((canvas, index) => {
        const dataUrl = canvas.toDataURL('image/png', 1.0)
        const breakStyle = index < canvases.length - 1 ? 'page-break-after: always;' : 'page-break-after: auto;'
        html += `<div style="width: 210mm; height: 297mm; ${breakStyle}">`
        html += `<img src="${dataUrl}" style="width: 100%; height: 100%; object-fit: contain; display: block;" />`
        html += '</div>'
      })
      return html
    },

    // A fixed delay only lets Vue's reactivity settle before we look at the
    // canvases; it doesn't guarantee the (possibly slow) background image
    // has actually finished loading. Poll PrintDocuments' `loading` flag
    // after the fixed delay so a slow-but-eventually-successful load can't
    // get captured while the canvas is still blank.
    async waitForDocumentImageReady (extraTimeoutMs = 3000, pollIntervalMs = 100) {
      const start = Date.now()
      while (this.$refs.printDocumentsComponent?.loading) {
        if (Date.now() - start >= extraTimeoutMs) {
          return false
        }
        await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
      }
      return true
    },

    printCertificatesOnlyHtml (certificatesHtml) {
      if (!certificatesHtml) return
      const newWin = window.open('')
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <title>Certificates</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { margin: 0; padding: 0; }
            @media print {
              @page { size: A4; margin: 0; }
              body { margin: 0 !important; padding: 0 !important; }
            }
          </style>
        </head>
        <body>${certificatesHtml}</body>
        </html>
      `
      newWin.document.write(htmlContent)
      newWin.document.close()
      setTimeout(() => {
        newWin.print()
        newWin.close()
      }, 500)
    },

    getBracketTitle (bracket) {
      const categoryPart = bracket.category?.name || ''
      const disciplinePart = bracket.discipline?.name || ''
      const titleParts = [categoryPart, disciplinePart].filter(Boolean).join(' → ')
      if (!titleParts) return 'Bracket'
      if (bracket.gender && bracket.gender !== 'mixed') {
        return `${titleParts} - ${bracket.gender === 'male' ? 'Muški' : 'Ženski'}`
      }
      return titleParts
    },

    async handleResultSubmitted () {
      // Refetch brackets after match result is submitted
      await this.loadBrackets()
    },

    async handleLock () {
      try {
        await this.$confirm(
          this.$tc('tournament.brackets_lock_warning'),
          this.$tc('general.warning'),
          {
            confirmButtonText: this.$tc('general.confirm'),
            cancelButtonText: this.$tc('general.cancel'),
            type: 'info'
          }
        )

        this.locking = true
        this.setPageData()
        try {
          await this.$store.dispatch('tournaments/lockBrackets', this.tournamentId)
          this.$message.success(
            this.$tc('tournament.brackets_locked_successfully')
          )
          await this.reloadTournament()
          await this.loadBrackets()
        } catch (e) {
          console.error(e)
          const errorMessage =
            e.response?.data?.error ||
            this.$tc('tournament.brackets_lock_failed')
          this.$message.error(errorMessage)
        } finally {
          this.locking = false
          this.setPageData()
        }
      } catch (error) {
        // User cancelled
      }
    },

    async handleUnlock () {
      try {
        await this.$confirm(
          this.$tc('tournament.brackets_unlock_warning'),
          this.$tc('general.warning'),
          {
            confirmButtonText: this.$tc('general.confirm'),
            cancelButtonText: this.$tc('general.cancel'),
            type: 'warning'
          }
        )

        this.unlocking = true
        this.setPageData()
        try {
          await this.$store.dispatch('tournaments/unlockBrackets', this.tournamentId)
          this.$message.success(
            this.$tc('tournament.brackets_unlocked_successfully')
          )
          await this.reloadTournament()
          await this.loadBrackets()
        } catch (e) {
          console.error(e)
          const errorMessage =
            e.response?.data?.error ||
            this.$tc('tournament.brackets_unlock_failed')
          this.$message.error(errorMessage)
        } finally {
          this.unlocking = false
          this.setPageData()
        }
      } catch (error) {
        // User cancelled
      }
    },

    handlePrint () {
      this.showPrintTypeModal = true
    },

    openPrintPreferencesModal (context) {
      this.printActionContext = context
      this.pendingPrintTableOptions = {
        showCompetitorImage: true,
        showClubLogo: true
      }
      this.showPrintPreferencesModal = true
    },

    closePrintPreferencesModal () {
      this.showPrintPreferencesModal = false
      this.printActionContext = null
    },

    confirmPrintPreferences () {
      this.printTableOptions = {
        showCompetitorImage: this.pendingPrintTableOptions.showCompetitorImage,
        showClubLogo: this.pendingPrintTableOptions.showClubLogo
      }
      const context = this.printActionContext
      this.showPrintPreferencesModal = false
      this.printActionContext = null

      this.$nextTick(() => {
        if (!context) return
        if (context.type === 'single-bracket' && context.bracket) {
          this.handlePrintSingleBracket(context.bracket)
          return
        }
        if (context.type === 'batch-brackets-after-target') {
          this.showPrintModal = true
        }
      })
    },

    handlePrintTypeSelect (target) {
      this.selectedPrintTarget = target
      this.showPrintTypeModal = false
      this.$nextTick(() => {
        if (target === 'brackets') {
          this.openPrintPreferencesModal({ type: 'batch-brackets-after-target' })
          return
        }
        this.showPrintModal = true
      })
    },

    handlePrintModalClose () {
      this.showPrintModal = false
    },

    handlePrintSelection (config) {
      // Track printed items in localStorage only for bracket print mode
      if (
        this.selectedPrintTarget === 'brackets' &&
        this.tournamentId &&
        config.selectedIds &&
        config.selectedIds.length > 0
      ) {
        const storageKey = `bracket_printed_${this.tournamentId}_${config.type}`
        const existingPrinted = JSON.parse(localStorage.getItem(storageKey) || '[]')
        const newPrinted = [...new Set([...existingPrinted, ...config.selectedIds])]
        localStorage.setItem(storageKey, JSON.stringify(newPrinted))
      }

      // Close the modal first
      this.showPrintModal = false

      // Store print configuration and wait for modal to close
      this.$nextTick(() => {
        this.printConfig = config

        // Wait for filtered layout to render
        this.$nextTick(() => {
          // Wait one more tick to ensure everything is rendered
          this.$nextTick(() => {
            this.executePrint()
          })
        })
      })
    },

    getFilteredBrackets () {
      if (!this.printConfig) return []

      const printItems = this.getPrintItems()

      // Extract all brackets from print items
      const brackets = []
      printItems.forEach(item => {
        if (item.brackets && Array.isArray(item.brackets)) {
          brackets.push(...item.brackets)
        }
      })

      return brackets
    },

    executePrint () {
      // Get filtered brackets based on print config
      const filteredBrackets = this.getFilteredBrackets()

      if (!filteredBrackets || filteredBrackets.length === 0) {
        console.error('No brackets to print')
        this.printConfig = null
        return
      }

      if (this.selectedPrintTarget === 'placements') {
        this.executePlacementPrint(filteredBrackets)
        return
      }

      // Generate HTML for each bracket and combine them
      let allBracketsHTML = ''

      filteredBrackets.forEach((bracket, index) => {
        const disciplineName = this.getDisciplineName(bracket)
        const categoryName = bracket.category?.name || ''

        const placementLabels = {}
        if (bracket.placement) {
          if (bracket.placement.first) placementLabels.first = this.getPrintPlacementLabel(bracket, 'first')
          if (bracket.placement.second) placementLabels.second = this.getPrintPlacementLabel(bracket, 'second')
          if (bracket.placement.third) placementLabels.third = this.getPrintPlacementLabel(bracket, 'third')
          if (bracket.placement.third2) placementLabels.third2 = this.getPrintPlacementLabel(bracket, 'third2')
        }

        const isScoring = this.getBracketDrawModel(bracket) === 'scoring'
        let bracketHTML
        if (isScoring) {
          const competitorNames = this.getBodovanjeCompetitors(bracket).map(c => this.getBodovanjeCompetitorName(c))
          bracketHTML = generateScoringPrintHTML(
            bracket,
            placementLabels,
            this.tournament,
            categoryName,
            disciplineName,
            this.bracketTatami[bracket.id],
            competitorNames
          )
        } else {
          bracketHTML = generateBracketPrintHTML(
            bracket,
            placementLabels,
            this.tournament,
            null,
            categoryName,
            disciplineName,
            this.bracketTatami[bracket.id]
          )
        }

        if (index > 0) {
          allBracketsHTML += '<div style="page-break-before: always; padding-top: 15mm;">'
        }
        allBracketsHTML += bracketHTML
        if (index > 0) {
          allBracketsHTML += '</div>'
        }

        // After cloning the bracket, add competitors table
        // This must happen AFTER the bracket clone is appended
        // Prefer visible table, fallback to print-filtered-layout table
        const competitorsSource = this.$el.querySelector(`#competitors-table-visible-${bracket.id}`) ||
                                  this.$el.querySelector(`#competitors-table-for-print-${bracket.id}`)

        if (competitorsSource) {
          const tableClone = competitorsSource.cloneNode(true)

          const pageBreak = document.createElement('div')
          pageBreak.style.pageBreakBefore = 'always'

          // Create a temporary container to get the HTML
          const tempContainer = document.createElement('div')
          tempContainer.appendChild(pageBreak)
          tempContainer.appendChild(tableClone)

          allBracketsHTML += tempContainer.innerHTML
        }
      })

      // Create a temporary element with the combined HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = allBracketsHTML
      tempDiv.style.display = 'none'
      document.body.appendChild(tempDiv)

      // Use openPrint to create new window and print
      const printTitle = this.tournament?.name || 'Brackets'
      openPrint(printTitle, tempDiv)

      // Clean up
      setTimeout(() => {
        document.body.removeChild(tempDiv)
        this.printConfig = null
      }, 1000)
    },

    getPlacementPrintHtml (bracket) {
      const rows = this.getPlacementRowsForBracket(bracket)
      if (!rows.length) return ''

      const categoryName = this.escapeHtml(bracket.category?.name || '')
      const disciplineName = this.escapeHtml(this.getDisciplineName(bracket) || '')
      const titleParts = [categoryName, disciplineName].filter(Boolean).join(' → ')

      const rowsHtml = rows.map(row => {
        const position = this.escapeHtml(row.positionLabel)
        const firstName = this.escapeHtml(row.firstName)
        const lastName = this.escapeHtml(row.lastName)
        const club = this.escapeHtml(row.club)
        const medal = this.escapeHtml(row.medal)
        return `<li class="placement-print-item"><span>${position} ${firstName}, ${lastName}, ${club}, ${medal}</span></li>`
      }).join('')

      return `
        <style>
          .placement-print-root {
            padding: 0 10mm;
            box-sizing: border-box;
          }
          .placement-print-header {
            margin-bottom: 30px;
            padding: 0 0 15px 0;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            position: relative;
          }
          .placement-print-header::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            border-bottom: 2px solid #333;
          }
          .placement-print-title {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
            color: #555;
          }
          .placement-print-list {
            margin: 0;
            padding-left: 0;
            list-style: none;
          }
          .placement-print-item {
            margin-bottom: 10px;
            font-size: 16px;
            line-height: 1.4;
            color: #222;
          }
        </style>
        <div class="placement-print-root">
          <div class="placement-print-header">
            <h2 class="placement-print-title">${titleParts}</h2>
          </div>
          <ol class="placement-print-list">${rowsHtml}</ol>
        </div>
      `
    },

    executePlacementPrint (filteredBrackets) {
      const placementSections = filteredBrackets
        .map(bracket => this.getPlacementPrintHtml(bracket))
        .filter(Boolean)

      if (placementSections.length === 0) {
        this.$message.warning('Nema dodeljenih plasmana za odabrane žrebove.')
        this.printConfig = null
        return
      }

      const placementHtml = placementSections
        .map((section, index) => {
          if (index === 0) return section
          return `<div style="page-break-before: always; padding-top: 15mm;">${section}</div>`
        })
        .join('')

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = placementHtml
      tempDiv.style.display = 'none'
      document.body.appendChild(tempDiv)

      const printTitle = this.tournament?.name ? `${this.tournament.name} - Plasman` : 'Plasman'
      openPrint(printTitle, tempDiv)

      setTimeout(() => {
        if (document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv)
        }
        this.printConfig = null
      }, 1000)
    },

    applyPrintScaling () {
      this.$nextTick(() => {
        // A4 portrait dimensions in mm (with 10mm margins)
        // A4 portrait: 210mm x 297mm
        // Usable area with 10mm margins: 190mm x 277mm
        const maxWidth = 190 // mm
        const maxHeight = 277 // mm

        // Convert mm to pixels (assuming 96 DPI: 1mm ≈ 3.7795px)
        const mmToPx = 3.7795
        const maxWidthPx = maxWidth * mmToPx
        const maxHeightPx = maxHeight * mmToPx

        // Scale bracket cards in normal layout
        const bracketCards = this.$el.querySelectorAll('.brackets-content .bracket-card')
        bracketCards.forEach(card => {
          const bracketDisplay = card.querySelector('.bracket-display')
          const bracketHeader = card.querySelector('.bracket-card__header')
          if (!bracketDisplay) return

          // Get header height (if present) - already in pixels
          const headerHeight = bracketHeader ? bracketHeader.getBoundingClientRect().height : 0

          // Available height for bracket = max height - header height - spacing (10mm)
          const spacingPx = 10 * mmToPx
          const availableHeight = maxHeightPx - headerHeight - spacingPx

          // Get actual bracket dimensions
          const bracketRect = bracketDisplay.getBoundingClientRect()
          const bracketWidth = bracketRect.width
          const bracketHeight = bracketRect.height

          // Calculate scale factors
          const scaleX = maxWidthPx / bracketWidth
          const scaleY = availableHeight / bracketHeight

          // Use the smaller scale to ensure it fits both dimensions
          // Don't scale up, only down
          const scale = Math.min(scaleX, scaleY, 1)

          // Apply scaling to the bracket display
          bracketDisplay.style.transform = `scale(${scale})`
          bracketDisplay.style.transformOrigin = 'top left'

          // Store original dimensions for cleanup
          bracketDisplay.dataset.originalWidth = bracketWidth
          bracketDisplay.dataset.originalHeight = bracketHeight
        })

        // Scale bracket displays in filtered layout
        const filteredBracketDisplays = this.$el.querySelectorAll('.print-filtered-layout .bracket-display')
        filteredBracketDisplays.forEach(bracketDisplay => {
          if (!bracketDisplay) return

          // Get actual bracket dimensions
          const bracketRect = bracketDisplay.getBoundingClientRect()
          const bracketWidth = bracketRect.width
          const bracketHeight = bracketRect.height

          // Calculate scale factors
          const scaleX = maxWidthPx / bracketWidth
          const scaleY = maxHeightPx / bracketHeight

          // Use the smaller scale to ensure it fits both dimensions
          // Don't scale up, only down
          const scale = Math.min(scaleX, scaleY, 1)

          // Apply scaling to the bracket display
          bracketDisplay.style.transform = `scale(${scale})`
          bracketDisplay.style.transformOrigin = 'top left'

          // Store original dimensions for cleanup
          bracketDisplay.dataset.originalWidth = bracketWidth
          bracketDisplay.dataset.originalHeight = bracketHeight
        })
      })
    },

    removePrintScaling () {
      const bracketDisplays = this.$el.querySelectorAll('.bracket-display')
      bracketDisplays.forEach(display => {
        display.style.transform = ''
        display.style.transformOrigin = ''
        delete display.dataset.originalWidth
        delete display.dataset.originalHeight
      })
    },

    setBracketFilter (filter) {
      // Toggle filter: if clicking the same filter, clear it (show all)
      if (this.bracketFilter === filter) {
        this.bracketFilter = null
      } else {
        this.bracketFilter = filter
      }
    },

    // Get rounds for a bracket (for print layout)
    getBracketRounds (bracket) {
      const matches = bracket.matches || []
      const roundsMap = {}

      matches.forEach(match => {
        if (!roundsMap[match.round]) {
          roundsMap[match.round] = {
            number: match.round,
            matches: []
          }
        }
        roundsMap[match.round].matches.push(match)
      })

      // Sort matches within each round by matchNumber
      Object.keys(roundsMap).forEach(roundNum => {
        roundsMap[roundNum].matches.sort((a, b) => {
          return (a.matchNumber || 0) - (b.matchNumber || 0)
        })
      })

      // Convert to array and sort by round number
      return Object.values(roundsMap).sort((a, b) => a.number - b.number)
    },

    // Get round name for print
    getRoundName (roundNumber, bracket) {
      const rounds = this.getBracketRounds(bracket)
      const totalRounds = rounds.length

      if (roundNumber === totalRounds) {
        return 'Finale'
      } else if (roundNumber === totalRounds - 1) {
        return 'Polufinale'
      } else {
        return this.$tc('tournament.eliminations')
      }
    },

    // Get match label for print (continuous across all brackets)
    getMatchLabelForPrint (match, bracket) {
      // Calculate label index across all brackets
      let labelIndex = 0
      for (const b of this.filteredBrackets) {
        const rounds = this.getBracketRounds(b)
        for (const round of rounds) {
          for (const m of round.matches) {
            if (m.id === match.id) {
              return String.fromCharCode(65 + labelIndex) // A, B, C, etc.
            }
            labelIndex++
          }
        }
      }
      return null
    },

    isMedalHolder (competitorReg) {
      if (!competitorReg) return false
      return !!(competitorReg.is_medal_holder || competitorReg.is_seeded || competitorReg.isSeeded)
    },

    // Get competitor name for print
    getCompetitorNameForPrint (competitorReg) {
      if (!competitorReg) {
        return this.$tc('tournament.unknown')
      }

      // If this is a team registration, display team members
      if (competitorReg.isTeam && competitorReg.teamMembers && competitorReg.teamMembers.length > 0) {
        const teamMembers = competitorReg.teamMembers.map(member => {
          const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
          const gender = member.isMale ? 'M' : 'Ž'
          return `${name} (${gender})`
        }).join(', ')

        return teamMembers
      }

      // Individual competitor display
      if (!competitorReg.competitor) {
        return this.$tc('tournament.unknown')
      }

      const competitor = competitorReg.competitor
      const name = `${competitor.firstName || ''} ${competitor.lastName || ''}`.trim()
      const gender = competitor.isMale === true ? ' (M)' : (competitor.isMale === false ? ' (Ž)' : '')
      const assignedNumber = competitorReg.assignedNumber ? ` ${competitorReg.assignedNumber}` : ''

      return name ? `${name}${gender}${assignedNumber}` : this.$tc('tournament.unknown')
    },

    getClubNameForPrint (competitorReg) {
      if (!competitorReg || !competitorReg.club) return ''
      const club = competitorReg.club
      return (club.shortName || club.name || '').trim()
    },

    getClubFullNameForPrint (competitorReg) {
      if (!competitorReg || !competitorReg.club) return ''
      const club = competitorReg.club
      return (club.name || club.shortName || '').trim()
    },

    isTeamCompetitor (competitorReg) {
      return !!(
        competitorReg &&
        competitorReg.isTeam &&
        competitorReg.teamMembers &&
        competitorReg.teamMembers.length > 0
      )
    },

    // Get placeholder text for print
    getPlaceholderTextForPrint (match, bracket, slotNumber) {
      const rounds = this.getBracketRounds(bracket)
      const roundIndex = rounds.findIndex(r => r.number === match.round)

      // Only show placeholders in rounds after Round 1
      if (roundIndex === 0) {
        return null
      }

      // Find source matches from previous round
      const previousRound = rounds[roundIndex - 1]
      if (!previousRound) return null

      const sourceMatches = previousRound.matches.filter(m => m.nextMatchId === match.id)
      sourceMatches.sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0))

      if (sourceMatches.length >= slotNumber) {
        const sourceMatch = sourceMatches[slotNumber - 1]
        const sourceLabel = this.getMatchLabelForPrint(sourceMatch, bracket)
        if (sourceLabel) {
          return `${this.$tc('tournament.winner_of_match')} ${sourceLabel}`
        }
      }

      return null
    },

    getPrintItems () {
      if (!this.printConfig) return []

      const { type, selectedIds, selectedPairs } = this.printConfig

      if (type === 'disciplines' && selectedPairs && selectedPairs.length > 0) {
        return this.getPrintItemsByCategoryDisciplinePairs(selectedPairs)
      } else if (type === 'categories') {
        return this.getPrintItemsByCategories(selectedIds)
      }

      return []
    },

    getPrintItemsByCategoryDisciplinePairs (selectedPairs) {
      const items = []

      selectedPairs.forEach(({ categoryId, disciplineId }) => {
        const brackets = this.filteredBrackets.filter(bracket => {
          const bracketCategoryId = bracket.category?.id
          const bracketDiscipline = bracket.discipline || bracket.teamDiscipline
          const bracketDisciplineId = bracketDiscipline && (bracketDiscipline.id ?? bracketDiscipline)
          const matchCategory = bracketCategoryId != null && Number(bracketCategoryId) === Number(categoryId)
          const matchDiscipline = bracketDisciplineId != null && Number(bracketDisciplineId) === Number(disciplineId)
          return matchCategory && matchDiscipline
        })

        if (brackets.length > 0) {
          const discipline = brackets[0].discipline || brackets[0].teamDiscipline
          const disciplineName = discipline?.name || `Discipline ${disciplineId}`
          items.push({
            id: `${categoryId}-${disciplineId}`,
            name: disciplineName,
            brackets: brackets
          })
        }
      })

      return items
    },

    getPrintItemsByCategories (categoryIds) {
      const items = []

      categoryIds.forEach(categoryId => {
        const brackets = this.filteredBrackets.filter(bracket => {
          return bracket.category && bracket.category.id === categoryId
        })

        if (brackets.length > 0) {
          items.push({
            id: categoryId,
            name: brackets[0].category?.name || `Category ${categoryId}`,
            brackets: brackets
          })
        }
      })

      return items
    },

    getBracketPrintTitle (bracket) {
      const parts = []

      // Add discipline name
      const discipline = bracket.discipline || bracket.teamDiscipline
      if (discipline?.name) {
        parts.push(discipline.name)
      }

      // Add category name
      if (bracket.category?.name) {
        parts.push(bracket.category.name)
      }

      // Add gender
      if (bracket.gender && bracket.gender !== 'mixed') {
        parts.push(bracket.gender === 'male' ? 'Muški' : 'Ženski')
      }

      return parts.join(' - ') || 'Bracket'
    },

    getGenderText (bracket) {
      if (!bracket.gender || bracket.gender === 'mixed') {
        return ''
      }
      return bracket.gender === 'male' ? 'Muški' : 'Ženski'
    },

    getDisciplineName (bracket) {
      const discipline = bracket.discipline || bracket.teamDiscipline
      if (discipline?.name) {
        return discipline.name
      }
      return 'Discipline'
    },

    isBracketGeneralRanking (bracket) {
      return this.bracketGeneralRanking[bracket.id] === true
    },

    getBracketDrawModel (bracket) {
      return this.bracketDrawModel[bracket.id] || 'skeleton'
    },

    getBodovanjeCompetitors (bracket) {
      const list = this.getBracketCompetitors(bracket)
      const orderIds = this.bracketScoringOrder[bracket.id]
      if (!orderIds || !Array.isArray(orderIds) || orderIds.length === 0) return list
      const byId = {}
      list.forEach(c => { byId[String(c._id || c.id)] = c })
      const ordered = []
      orderIds.forEach(id => {
        if (byId[id]) ordered.push(byId[id])
      })
      if (ordered.length !== list.length) return list
      return ordered
    },

    getBodovanjeCompetitorName (competitor) {
      if (!competitor) return '—'
      if ((competitor.isTeam || (competitor.teamMembers && competitor.teamMembers.length > 0)) && competitor.teamMembers?.length) {
        const names = competitor.teamMembers.map(m => {
          const memberName = `${m.firstName || ''} ${m.lastName || ''}`.trim()
          const memberGender = m.isMale === true ? ' (M)' : (m.isMale === false ? ' (Ž)' : '')
          return `${memberName}${memberGender}`
        }).join(', ')
        const num = competitor.assignedNumber ? ` (#${competitor.assignedNumber})` : ''
        return names ? `${names}${num}` : '—'
      }
      const firstName = competitor.competitor?.firstName || ''
      const lastName = competitor.competitor?.lastName || ''
      const name = `${firstName} ${lastName}`.trim()
      const isMale = competitor.competitor?.isMale
      const gender = isMale === true ? ' (M)' : (isMale === false ? ' (Ž)' : '')
      const num = competitor.assignedNumber ? ` (#${competitor.assignedNumber})` : ''
      return name ? `${name}${gender}${num}` : '—'
    },

    getBracketCompetitors (bracket) {
      // Get all competitors from the first round of the bracket
      const competitors = []
      const competitorIds = new Set()

      if (!bracket.matches || bracket.matches.length === 0) {
        return []
      }

      // Find the first round
      const rounds = this.getBracketRounds(bracket)
      if (rounds.length === 0) return []

      const firstRound = rounds[0]
      const isTeam = this.isTeamBracket(bracket)
      const allRegistrations = isTeam ? this.teams : this.competitors

      firstRound.matches.forEach(match => {
        // Prefer full registration data from loaded registrations for individual brackets.
        // For team brackets, the teams endpoint does not include `teamMembers`, so prefer match data.
        let competitorReg = null
        let competitorTwoReg = null

        // Look up full registration data from loaded registrations
        if (match.competitorRegId) {
          const regFromLoaded = allRegistrations.find(reg =>
            (reg._id && String(reg._id) === String(match.competitorRegId)) ||
            (reg.id && String(reg.id) === String(match.competitorRegId))
          )
          const regFromMatch = match.competitorReg

          competitorReg = isTeam
            ? (regFromMatch && regFromMatch.teamMembers && regFromMatch.teamMembers.length > 0
              ? regFromMatch
              : (regFromLoaded || regFromMatch))
            : (regFromLoaded || regFromMatch)
        }

        if (match.competitorTwoRegId) {
          const regFromLoaded = allRegistrations.find(reg =>
            (reg._id && String(reg._id) === String(match.competitorTwoRegId)) ||
            (reg.id && String(reg.id) === String(match.competitorTwoRegId))
          )
          const regFromMatch = match.competitorTwoReg

          competitorTwoReg = isTeam
            ? (regFromMatch && regFromMatch.teamMembers && regFromMatch.teamMembers.length > 0
              ? regFromMatch
              : (regFromLoaded || regFromMatch))
            : (regFromLoaded || regFromMatch)
        }

        // Add first competitor
        if (competitorReg && match.competitorRegId && !competitorIds.has(match.competitorRegId)) {
          const competitor = { ...competitorReg, _id: match.competitorRegId }
          competitors.push(competitor)
          competitorIds.add(match.competitorRegId)
        }

        // Add second competitor
        if (competitorTwoReg && match.competitorTwoRegId && !competitorIds.has(match.competitorTwoRegId)) {
          const competitor = { ...competitorTwoReg, _id: match.competitorTwoRegId }
          competitors.push(competitor)
          competitorIds.add(match.competitorTwoRegId)
        }
      })

      // Sort by assigned number if available, then by name
      return competitors.sort((a, b) => {
        const numA = a.assignedNumber ? parseInt(a.assignedNumber) : 9999
        const numB = b.assignedNumber ? parseInt(b.assignedNumber) : 9999
        if (numA !== numB) return numA - numB

        const nameA = a.competitor ? `${a.competitor.firstName || ''} ${a.competitor.lastName || ''}`.trim() : ''
        const nameB = b.competitor ? `${b.competitor.firstName || ''} ${b.competitor.lastName || ''}`.trim() : ''
        return nameA.localeCompare(nameB)
      })
    },

    getPrintPlacementCount (bracket) {
      const count = this.getBracketCompetitors(bracket).length
      if (count === 0) return 0
      if (count === 1) return 1
      if (count === 2) return 2
      if (count === 3) return 3
      return 4
    },

    hasLargeBracket (bracket) {
      const rounds = this.getBracketRounds(bracket)
      return rounds.length > 0 && rounds[0].matches.length >= 8
    },

    getPrintPlacementRegistration (bracket, placementKey) {
      const regId = bracket?.placement && bracket.placement[placementKey]
      if (regId == null) return null
      const list = this.getBracketCompetitors(bracket)
      return list.find(c => c._id != null && String(c._id) === String(regId)) || null
    },

    getPlacementRegistrationIds (bracket, placementKey) {
      const ids = []
      const legacyId = bracket?.placement && bracket.placement[placementKey]
      if (legacyId != null && legacyId !== '') {
        ids.push(legacyId)
      }
      const multiIds = bracket?.placement_multi && Array.isArray(bracket.placement_multi[placementKey])
        ? bracket.placement_multi[placementKey]
        : []
      multiIds.forEach(id => {
        if (id != null && id !== '' && !ids.some(existing => String(existing) === String(id))) {
          ids.push(id)
        }
      })
      return ids
    },

    getPlacementRowsForBracket (bracket) {
      if (!bracket) return []
      const list = this.getBracketCompetitors(bracket)
      const placementOrder = [
        { key: 'first', label: '1.', medal: '🥇' },
        { key: 'second', label: '2.', medal: '🥈' },
        { key: 'third', label: '3.', medal: '🥉' },
        { key: 'third2', label: '3.', medal: '🥉' }
      ]
      const rows = []

      placementOrder.forEach(item => {
        const ids = this.getPlacementRegistrationIds(bracket, item.key)
        ids.forEach(regId => {
          const reg = list.find(c => c._id != null && String(c._id) === String(regId))
          if (!reg) return

          let firstName = ''
          let lastName = ''
          if (reg.competitor) {
            firstName = reg.competitor.firstName || ''
            lastName = reg.competitor.lastName || ''
          } else if (reg.isTeam && Array.isArray(reg.teamMembers) && reg.teamMembers.length > 0) {
            const teamNames = reg.teamMembers
              .map(m => `${m.firstName || ''} ${m.lastName || ''}`.trim())
              .filter(Boolean)
              .join(', ')
            firstName = teamNames
            lastName = ''
          }

          rows.push({
            positionLabel: item.label,
            firstName,
            lastName,
            club: this.getClubNameForPrint(reg) || '',
            medal: item.medal
          })
        })
      })

      return rows
    },

    escapeHtml (text) {
      if (text == null) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },

    getPlacementMedal (placementKey) {
      const medalByPlacement = {
        first: '🥇',
        second: '🥈',
        third: '🥉',
        third2: '🥉'
      }
      return medalByPlacement[placementKey] || ''
    },

    getPrintPlacementPrimaryLine (bracket, placementKey) {
      const reg = this.getPrintPlacementRegistration(bracket, placementKey)
      if (!reg) return ''
      const medal = this.getPlacementMedal(placementKey)

      if (reg.isTeam && reg.teamMembers && reg.teamMembers.length > 0) {
        const teamMembersStr = reg.teamMembers
          .map(m => {
            const name = `${m.firstName || ''} ${m.lastName || ''}`.trim()
            const gender = m.isMale ? 'M' : 'Ž'
            return `${name} (${gender})`
          })
          .filter(Boolean)
          .join(', ')
        return medal ? `${medal} ${teamMembersStr}` : teamMembersStr
      }

      if (!reg.competitor) return ''
      const name = `${reg.competitor.firstName || ''} ${reg.competitor.lastName || ''}`.trim()
      const num = reg.assignedNumber ? ` (#${reg.assignedNumber})` : ''
      const label = name ? `${name}${num}` : ''
      return medal ? `${medal} ${label}` : label
    },

    getPrintPlacementSecondaryLine (bracket, placementKey) {
      const reg = this.getPrintPlacementRegistration(bracket, placementKey)
      if (!reg) return ''
      return this.getClubNameForPrint(reg)
    },

    getPrintPlacementLabel (bracket, placementKey) {
      const reg = this.getPrintPlacementRegistration(bracket, placementKey)
      if (!reg) return ''
      const medal = this.getPlacementMedal(placementKey)
      const clubName = this.getClubNameForPrint(reg)
      if (reg.isTeam && reg.teamMembers && reg.teamMembers.length > 0) {
        const teamMembersStr = reg.teamMembers
          .map(m => {
            const name = `${m.firstName || ''} ${m.lastName || ''}`.trim()
            const gender = m.isMale ? 'M' : 'Ž'
            return `${name} (${gender})`
          })
          .filter(Boolean)
          .join(', ')
        const primary = medal ? `${medal} ${teamMembersStr}` : teamMembersStr
        return clubName ? `${primary}\n${clubName}` : primary
      }
      if (!reg.competitor) return ''
      const name = `${reg.competitor.firstName || ''} ${reg.competitor.lastName || ''}`.trim()
      const num = reg.assignedNumber ? ` (#${reg.assignedNumber})` : ''
      const label = name ? `${name}${num}` : ''
      const primary = medal ? `${medal} ${label}` : label
      return clubName ? `${primary}\n${clubName}` : primary
    },

    isTeamBracket (bracket) {
      return bracket.teamDiscipline !== null &&
             bracket.teamDiscipline !== undefined &&
             bracket.teamDiscipline !== '' &&
             bracket.teamDiscipline !== 0
    }
  }
}
</script>

<style lang="scss" scoped>
.bracket-controls {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.bracket-filters {
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid #e0e0e0;
  border-radius: 22px;
  overflow: hidden;
  background: #f8f8f8;
}

.bracket-filters .el-button {
  height: 44px;
  width: 150px;
  border: none;
  border-right: 1px solid #e0e0e0;
  margin: 0;
  background: #f8f8f8;
  color: #4d4d4d;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 700;
  transition: background 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:first-child {
    border-top-left-radius: 22px;
    border-bottom-left-radius: 22px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:last-child {
    border-top-right-radius: 22px;
    border-bottom-right-radius: 22px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-right: none;
  }

  &:hover:not(.filter-active) {
    background: #f0f0f0;
    color: #4d4d4d;
  }

  &:focus:not(.filter-active) {
    outline: 2px solid #409eff;
    outline-offset: 2px;
  }

  &.filter-active,
  &.filter-active:hover {
    background-color: #fff;
    color: #4d4d4d;
  }

  &.filter-active:focus {
    outline: 2px solid #409eff;
    outline-offset: 2px;
  }
}

.bracket-content-container {
  margin-top: 20px;
}

.print-competitor {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.print-competitor-club {
  display: block;
  font-size: 12px;
  color: #606266;
}

.print-competitor-team {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.print-competitor-club-main {
  font-weight: 600;
  font-size: 14px;
  color: inherit;
}

.print-competitor-names-sub {
  font-size: 11px;
  color: #606266;
}

.category-group {
  margin-bottom: 40px;
}

.category-group__header {
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #3C485A;
}

.category-group__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #3C485A;
}

.bracket-card {
  margin-bottom: 24px;
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
  overflow: visible;
}

.bracket-card__header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f2f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.bracket-card__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.draw-model-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.draw-model-option-wrap {
  width: 100%;
  min-width: 0;
}

.draw-model-option-wrap .draw-model-option-btn {
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 12px 20px;
  box-sizing: border-box;
  display: flex !important;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* Force both primary and default el-button to share the same layout (avoids misalignment) */
.draw-model-dialog .draw-model-option-btn.el-button--primary,
.draw-model-dialog .draw-model-option-btn.el-button--default {
  width: 100%;
  margin: 0;
  padding: 12px 20px;
  box-sizing: border-box;
  border-width: 1px;
  min-height: 44px;
}

.draw-model-dialog-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.draw-model-dialog-footer .el-button:first-child {
  margin-right: auto;
}

.placement-modal-content {
  max-height: 420px;
  overflow: auto;
}

.placement-empty {
  padding: 16px;
  color: #606266;
}

.placement-table {
  width: 100%;
}

.placement-medal-icon {
  font-size: 24px;
  line-height: 1;
  display: inline-block;
}

.placement-dialog-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.draw-dropdown-btn {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  margin: 0;
  padding: 0;
  background: #3C485A;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
  font-size: 18px;
}

.draw-dropdown-btn:hover:not(:disabled) {
  background: #2d3745;
}

.draw-dropdown-btn:active:not(:disabled) {
  background: #252d38;
}

.draw-dropdown-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.draw-dropdown-btn i {
  font-size: 18px;
}

.tatami-label {
  color: #606266;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
}

.tatami-select {
  width: 90px;
}

.bracket-card__body {
  position: relative;
  padding: 10px 20px 20px 20px;
  overflow: visible;
}

.competitor-table-section {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e6e8ee;
}

.bodovanje-section {
  margin-top: 0;
}

.bodovanje-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.bodovanje-table th,
.bodovanje-table td {
  border: 1px solid #dcdfe6;
  padding: 8px 10px;
  text-align: left;
}

.bodovanje-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  text-align: center;
}

.bodovanje-table .bodovanje-col-rb {
  width: 40px;
  text-align: center;
}

.bodovanje-table .bodovanje-col-name {
  min-width: 160px;
}

.bodovanje-table .bodovanje-col-disciplina {
  min-width: 120px;
}

.bodovanje-table .bodovanje-col-score {
  width: 48px;
  text-align: center;
}

.bodovanje-table .bodovanje-col-zbir,
.bodovanje-table .bodovanje-col-plasman {
  width: 56px;
  text-align: center;
}

.bracket-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #3C485A;
  flex: 1;
}

.print-bracket-btn {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  margin: 0;
  background: #fff;
  color: #606266;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 18px;
}

.print-bracket-btn:hover {
  background: #f5f7fa;
  border-color: #c0c4cc;
}

.print-bracket-btn:active {
  background: #ecf5ff;
  border-color: #409eff;
}

.print-bracket-btn i {
  font-size: 18px;
}

.regenerate-bracket-btn {
  flex-shrink: 0;
  height: 44px;
  min-width: 180px;
  border: 1px solid #dcdfe6;
  border-radius: 22px;
  margin: 0;
  background: #fff;
  color: #606266;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #3C485A;
    border-color: #3C485A;
    color: #fff;
  }

  &:focus {
    outline: 2px solid #3C485A;
    outline-offset: 2px;
  }

  &:active {
    background: #2d3745;
    border-color: #2d3745;
  }
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-bracket-placeholder {
  width: 100%;
  margin-bottom: 20px;
}

.empty-bracket-placeholder__content {
  padding: 40px 20px;
  text-align: center;
  background: #fff;
  border: 1px solid #e6e8ee;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  color: #606266;
  font-size: 15px;
  line-height: 1.6;
}

.print-type-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.print-type-option-btn {
  width: 100%;
  margin-left: 0 !important;
}

.print-preferences-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

/* Signature section in print layout (visible when print layout is shown) */
.print-signature-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #333;
  width: 100%;
}

.print-signature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.print-signature-left {
  align-items: flex-start;
}

.print-signature-center {
  flex: 0 0 auto;
  align-items: center;
}

.print-signature-right {
  align-items: flex-end;
}

.print-signature-label {
  font-size: 12px;
  font-weight: 600;
  color: #000;
  margin-bottom: 20px;
  white-space: nowrap;
}

.print-signature-line {
  display: block;
  width: 120px;
  height: 0;
  border-bottom: 1px solid #333;
}

/* Placement section in print layout (empty boxes for manual entry) */
.print-placement-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #333;
  width: 100%;
}

.print-placement-title {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #000;
}

.print-placement-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
}

.print-placement-field {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.print-placement-label {
  font-size: 12px;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
  text-align: center;
}

.print-placement-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  min-height: 52px;
  border: 1px solid #333;
  background: #fff;
  padding: 3px 6px;
  text-align: center;
}

.print-placement-line {
  display: block;
  width: 100%;
  text-align: center;
  white-space: nowrap;
}

.print-placement-line-primary {
  font-size: 11px;
  line-height: 1.2;
  min-height: 1.2em;
}

.print-placement-line-secondary {
  font-size: 11px;
  line-height: 1.2;
  min-height: 2.4em;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  white-space: normal;
  word-break: break-word;
}

// Print styles - no margin so browser does not reserve space for header/footer
@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  body.printing-filtered {
    margin: 0;
    padding: 10mm;
  }
  // Hide all non-bracket UI elements
  ::v-deep .el-alert,
  ::v-deep .el-button,
  ::v-deep .el-dialog,
  ::v-deep .el-dialog__wrapper,
  ::v-deep .bracket-filters,
  ::v-deep .bracket-controls,
  ::v-deep .panel,
  ::v-deep .sidebar,
  ::v-deep .page-header,
  ::v-deep nav,
  ::v-deep .breadcrumb,
  ::v-deep header,
  ::v-deep .empty-bracket-placeholder,
  .empty-state,
  .regenerate-bracket-btn,
  .draw-dropdown-btn,
  .print-bracket-btn {
    display: none !important;
  }

  // Hide the vertical print layout
  .print-brackets-layout {
    display: none !important;
  }

  // When not using filtered layout, hide it in print
  .print-filtered-layout {
    display: none !important;
  }

  // When using filtered layout for print, show only that
  body.printing-filtered {
    .brackets-content {
      display: none !important;
    }

    .print-filtered-layout {
      display: block !important;
    }
  }

  // Show only the bracket container
  body {
    margin: 0;
    padding: 0;
    background: white;
  }

  .bracket-content-container {
    margin: 0;
    padding: 0;
  }

  .brackets-content {
    margin: 0;
    padding: 0;
  }

  // Bracket root container - prevent page breaks inside
  .bracket-root {
    page-break-inside: avoid;
    break-inside: avoid;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 20mm;
  }

  // Category group styling for print
  .category-group {
    margin-bottom: 15mm;
    page-break-inside: avoid;

    &:not(:first-child) {
      page-break-before: always;
    }
  }

  .category-group__header {
    margin-bottom: 8mm;
    padding-bottom: 3mm;
    border-bottom: 2px solid #000;
    page-break-after: avoid;
  }

  .category-group__title {
    margin: 0;
    font-size: 20pt;
    font-weight: bold;
    color: #000;
  }

  // Each bracket card - start new page for each bracket (except first)
  .bracket-card {
    page-break-inside: avoid;
    break-inside: avoid;
    border: none;
    box-shadow: none;
    margin: 0;
    padding: 0;
    background: white;
    page-break-before: auto;

    &:not(:first-child) {
      page-break-before: always;
    }
  }

  .bracket-card__header {
    margin-bottom: 10mm;
    padding: 0;
    border-bottom: 2px solid #000;
    page-break-after: avoid;

    .bracket-title {
      margin: 0 0 5mm 0;
      font-size: 18pt;
      font-weight: bold;
      color: #000;
    }
  }

  .bracket-card__body {
    padding: 0;
    margin: 0;
    overflow: visible !important;
    width: 100%;
    height: auto;
  }

  // Competitor table section - visible in print
  .competitor-table-section {
    display: block !important;
    visibility: visible !important;
    margin-top: 10mm !important;
    padding-top: 5mm !important;
    border-top: 1px solid #333 !important;
    page-break-before: auto;
    page-break-inside: avoid;

    // Override page-break-before from component to allow table right after bracket
    ::v-deep .competitor-list-section {
      page-break-before: auto !important;
      padding: 5mm 0 !important;
    }
  }

  // Ensure bracket display is visible and properly scaled
  ::v-deep .bracket-display {
    display: flex !important;
    overflow: visible !important;
    padding: 0 !important;
    gap: 40px !important;
    page-break-inside: avoid;
    break-inside: avoid;
    background: white !important;
    transform-origin: top left;
    width: 100%;
    height: auto;
  }

  ::v-deep .bracket-round {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  ::v-deep .match-container {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  ::v-deep .match-box {
    page-break-inside: avoid;
    break-inside: avoid;
    border: 1px solid #000 !important;
    box-shadow: none !important;
    background: white !important;
    break-after: auto;
  }

  // Print styles for filtered layout
  body.printing-filtered .print-filtered-layout {
    display: block !important;
  }

  .print-item-group {
    page-break-before: auto;

    &:not(:first-child) {
      page-break-before: always;
    }
  }

  .print-category-header {
    page-break-after: avoid;
    margin-bottom: 5mm;
    padding-bottom: 3mm;
    border-bottom: 3px solid #000;
  }

  .print-category-title {
    margin: 0;
    font-size: 22pt;
    font-weight: bold;
    color: #000;
  }

  .print-bracket-wrapper {
    page-break-before: always;
    page-break-inside: auto;
    width: 100%;

    &.is-first-discipline {
      page-break-before: auto;
    }
  }

  /* Keep discipline title and draw on same page(s); only competitor list starts new page */
  .print-discipline-block {
    page-break-inside: avoid;
    page-break-after: avoid;
  }

  .print-bracket-header {
    margin: 0 0 6mm 0;
    padding: 0 0 2mm 0;
    border-bottom: 2px solid #000;
    page-break-after: avoid;
    display: block !important;
    visibility: visible !important;
  }

  .print-discipline-title {
    margin: 0 0 2mm 0;
    font-size: 18pt;
    font-weight: bold;
    color: #000;
    display: block !important;
    visibility: visible !important;
  }

  .print-bracket-subtitle {
    display: none !important;
  }

  .print-bracket-title {
    margin: 0;
    font-size: 18pt;
    font-weight: bold;
    color: #000;
  }

  .print-bracket-display-wrapper {
    margin: 0;
    padding: 0;
    page-break-after: auto;
    page-break-inside: avoid;
    overflow: visible !important;
    width: 100%;

    ::v-deep .bracket-display-wrapper {
      overflow: visible !important;
      width: 100%;
    }

    ::v-deep .bracket-display {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      overflow: visible !important;
      padding: 10mm 5mm !important;
      gap: 30px !important;
      background: white !important;
      position: relative !important;
      width: 100%;
    }

    ::v-deep .bracket-connectors {
      display: block !important;
      visibility: visible !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      z-index: 1;
    }

    ::v-deep .connector-line {
      fill: none !important;
      stroke: #333 !important;
      stroke-width: 2 !important;
    }

    ::v-deep .bracket-round {
      page-break-inside: avoid;
      break-inside: avoid;
      min-width: 200px !important;
      flex-shrink: 0 !important;
    }

    ::v-deep .match-container {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    ::v-deep .match-box {
      page-break-inside: avoid;
      break-inside: avoid;
      border: 1px solid #000 !important;
      box-shadow: none !important;
      background: white !important;
    }

    ::v-deep .placement-section {
      display: none !important;
    }

    &.print-bracket-compact {
      ::v-deep .bracket-display {
        padding: 2mm 2mm !important;
        gap: 8px !important;
      }

      ::v-deep .bracket-round {
        min-width: 110px !important;
      }

      ::v-deep .match-box {
        min-height: 44px !important;
        padding: 3px 4px !important;
        font-size: 9px !important;
      }

      ::v-deep .round-header h4 {
        font-size: 10px !important;
        margin-bottom: 4px !important;
      }
    }
  }

  .print-bracket-wrapper.print-bracket-compact {
    .print-placement-section {
      margin-top: 3mm !important;
      padding-top: 3mm !important;
    }

    .print-placement-title {
      margin-bottom: 1mm !important;
    }

    .print-signature-section {
      margin-top: 4mm !important;
      padding-top: 3mm !important;
    }
  }

  .print-placement-section {
    margin-top: 6mm;
    padding-top: 4mm;
    border-top: 1px solid #333;
    page-break-after: avoid;
    page-break-inside: avoid;
    max-width: 100%;
    box-sizing: border-box;
  }

  .print-placement-title {
    margin: 0 0 2mm 0;
    font-size: 12pt;
    font-weight: 600;
  }

  .print-placement-field {
    flex: 1 1 0;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    gap: 1mm;
  }

  .print-placement-fields {
    display: flex;
    flex-wrap: nowrap;
    gap: 4mm 6mm;
    max-width: 100%;
  }

  .print-placement-box {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 15mm;
    box-sizing: border-box;
  }

  .print-signature-section {
    display: flex !important;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 8mm;
    padding-top: 4mm;
    border-top: 1px solid #333;
    page-break-after: avoid;
    page-break-inside: avoid;
    width: 100%;
    max-width: 100%;
  }

  .print-signature-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
  }

  .print-signature-left {
    align-self: flex-end;
    align-items: flex-start;
  }

  .print-signature-center {
    flex: 0 0 auto;
    align-items: center;
  }

  .print-signature-right {
    align-self: flex-end;
    align-items: flex-end;
  }

  .print-signature-label {
    font-size: 10pt;
    font-weight: 600;
    color: #000;
    margin-bottom: 10mm;
    white-space: nowrap;
  }

  .print-signature-line {
    display: block;
    width: 45mm;
    height: 0;
    border-bottom: 1px solid #333;
  }

  .print-signature-center .print-signature-line {
    width: 45mm;
  }

  // Ensure competitor list wrapper and section are visible in print
  .competitor-list-wrapper {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    page-break-before: always;
  }

  ::v-deep .competitor-list-section {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    page-break-before: always;
    page-break-inside: avoid;
    padding: 5mm 0;
    margin: 0 !important;
    width: 100% !important;
  }

  // Ensure competitor list is visible within print-bracket-wrapper
  .print-bracket-wrapper .competitor-list-wrapper,
  .print-bracket-wrapper ::v-deep .competitor-list-section {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .competitor-list-header {
    margin-bottom: 5mm;
    padding-bottom: 3mm;
    border-bottom: 2px solid #000;
  }

  ::v-deep .competitor-list-title {
    margin: 0 0 3mm 0;
    font-size: 16pt;
    font-weight: bold;
  }

  ::v-deep .competitor-list-subtitle {
    margin: 0 0 2mm 0;
    font-size: 12pt;
  }

  ::v-deep .competitor-count {
    font-size: 11pt;
  }

  ::v-deep .competitor-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 9pt;
  }

  ::v-deep .competitor-table th,
  ::v-deep .competitor-table td {
    border: none;
    border-bottom: 1px solid #333;
    padding: 6px 4px;
  }

  ::v-deep .competitor-table th {
    background-color: #f0f0f0;
    font-weight: bold;
  }

  ::v-deep .competitor-table thead {
    display: table-header-group;
  }

  ::v-deep .competitor-table tbody {
    display: table-row-group;
  }

  ::v-deep .competitor-table tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  ::v-deep .competitor-avatar {
    width: 50px;
    height: 50px;
  }
}
</style>

<style lang="scss">
/* Global print styles: hide all UI except draw content when printing bracket */
@media print {
  body.printing-filtered {
    .page-wrapper > div:first-child,
    .page-wrapper main > .panel-header,
    .page-wrapper main > .panel {
      display: none !important;
    }
    .page-wrapper main {
      padding: 0 !important;
      max-width: 100% !important;
    }
    .page-wrapper {
      padding: 0 !important;
    }
  }
}
</style>
